/**
 * Gantt calendar view. Doors as rows, time slots as columns across the top.
 * Appointments render as colored blocks positioned absolutely within each
 * door's row.
 *
 * Designed for facilities with many doors (30-80+). Each row is fixed
 * height; the time axis is fixed width; horizontal/vertical scroll is
 * handled by an outer overflow container.
 *
 * Interactions:
 *  - Click an appointment block → onSelectAppointment(appt)
 *  - Click-and-drag empty area of a row → onCreateInSlot
 *  - Press-and-drag an appointment block → onMoveAppointment (reschedule + door change)
 *  - Right-click an appointment block → onRequestContextMenu (page-level renders the menu)
 *
 * Click vs drag distinction: the gesture only commits as a drag if the cursor
 * moves more than DRAG_MIN_PX from its starting point. Anything shorter is
 * treated as a click and fires the select handler.
 */
/* ⭐ `ReactNode` joined 2026-09-27 for `resetButton` — the page builds Ⓡ and this
   file mounts it, so the type has to name a node rather than a handler. */
import { useMemo, useRef, useEffect, useState, useCallback, type ReactNode, type DragEvent as ReactDragEvent } from 'react';
import {
    CHECKIN_DND_MIME, CHECKIN_FROM_APPT_MIME, DOOR_ROW_DND_MIME, isDoorRowDrag,
    isPaperworkDrag, readPaperworkDrag, type PaperworkDragPayload, paperworkDragInProgress,
    isWmsOrderDrag, readWmsOrderDrag, wmsOrderDragInProgress, type WmsOrderDragKey,
} from '@/lib/dnd';
import { AlarmClockOff, ArrowDownWideNarrow, ArrowDownAZ, ArrowUpAZ, RotateCcw } from 'lucide-react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import type {
    AppointmentDto,
    AppointmentStatusCode,
    CalendarDoorDto,
    AppointmentEventDto,
} from '@/api/types';
import { cn, isDayOnly, hasActualSpan, FRAME_BORDER } from '@/lib/utils';
import { DirectionTag } from '@/components/DirectionTag';
import {
    orderDoors, moveDoorForDrop, canDropDoorRow, readDoorOrder, writeDoorOrder, clearDoorOrder, isSiteOrder,
    type DoorSortDir,
} from '@/lib/doorOrder';
import { StatusFilterBar, BOARD_HEADER_STRIP_CLASS } from '@/components/StatusFilterBar';
import { WorkFilterBar } from '@/components/WorkFilterBar';
/* ⭐⭐ THE THIRD AXIS AND ITS GLYPH (Pete, 2026-09-27). ⛔ The bar is a THIRD bar,
   not two more chips in the work row — chips inside one row are an OR, and
   "unloading AND no WMS" has to be askable. See `WmsFilterBar.tsx`. */
import { WmsFilterBar } from '@/components/WmsFilterBar';
import { CalendarIconKey } from '@/components/CalendarIconKey';
import { NoWmsBadge } from '@/components/WmsMarks';
import { isPossibleNoShow } from '@/lib/possibleNoShow';
import { DriverBadge, LaborPulse, LaborPausedMark, LaborDoneMark, LoadStateStamp, STAMP_MIN_WIDTH_PX, HoverTimeline, PaperworkBadge, LicenceBadge } from '@/components/AppointmentHoverCard';
import { loadSubLine } from '@/lib/loadIdentifier';
import { DoorCameraPlaceholder } from '@/components/LabourPanel';

// ---- Visual config --------------------------------------------------------
/**
 * THE HOUR COLUMN IS A FLOOR, NOT A FIXED WIDTH.
 *
 * The day grid used to be exactly 17 * 80px wide no matter how big the
 * monitor was, so on a wide screen the timeline stopped around 1360px and left
 * the right half of the page empty while the appointment blocks stayed too
 * narrow to read their own text. Week and Month already filled the window, so
 * Day was the odd one out.
 *
 * Now 80px is the MINIMUM. The component measures its scroll viewport and grows
 * the hour column to fill it, which widens every appointment block by the same
 * factor and gives the crushed labels room. Below the floor nothing shrinks —
 * it goes back to horizontal scrolling, which is what a laptop or a phone needs.
 */
const MIN_HOUR_WIDTH_PX = 80;
const ROW_HEIGHT_PX = 40;          // height of ONE lane; a single-occupancy door looks unchanged
const LANE_HEIGHT_PX = ROW_HEIGHT_PX; // a door's full row height = laneCount * LANE_HEIGHT_PX
/**
 * ---- ⭐ THE DOOR COLUMN BREATHES (Pete, 2026-09-19) ----
 *
 * > *"Also, site order needs to fit correctly"* → then, once it fitted:
 * > *"the door column now has too much wasted space. Can the site order only show
 * > when its not in site order and can the column expand and contract based on
 * > that scenario?"*
 *
 * ⛔⛔ I WIDENED IT FOR THE WORST CASE AND LEFT EVERY OTHER CASE PAYING FOR IT.
 * The `↺ Site order` reset is the only thing that needs 200px, and it renders ONLY
 * when the board is out of the site's order — which is the minority of the time.
 *
 *   ** A FIXED SIZE CHOSEN FOR A CONDITIONAL CHILD IS THAT CHILD'S COST, CHARGED
 *      TO EVERY RENDER THAT DOES NOT HAVE IT. **
 *
 * ✅ MEASURED, NOT GUESSED: `hourWidth = max(MIN_HOUR_WIDTH_PX, usable /
 * totalHours)` and `usable = viewportWidth - DOOR_LABEL_WIDTH_PX`, so the 40px
 * goes straight back to the timeline. ⛔⛔ WHICH IS WHY THE WIDTH IS NOW IN THAT
 * MEMO'S DEPENDENCY LIST. Leaving it out is the same defect as the missing
 * `totalHours` that produced Pete's *"White space on the right after 5PM"* — the
 * column would narrow and the hours would not grow to take the space.
 *
 * ⚠️ `WeekBoard.tsx` USES THE SAME TWO NUMBERS AND THE SAME CONDITION. Changing
 * one without the other is the drift this pair keeps producing; `door-order-test`
 * asserts they agree.
 */
const DOOR_LABEL_NARROW_PX = 160;   // DOORS (23) + the sort toggle
const DOOR_LABEL_WIDE_PX   = 200;   // ...plus the ` ↺ Site order` reset
const HEADER_HEIGHT_PX = 32;
/**
 * THE SHIPPED WINDOW, used only when the facility has no operating hours for
 * this weekday. Every real facility narrows it: Brighton on a Monday runs
 * 07:30-17:00 across both directions, so drawing 5 AM-10 PM spent nearly half
 * the grid on hours nothing can happen in, and halved the width of every
 * appointment block to pay for it.
 */
const FALLBACK_START_HOUR = 5;
const FALLBACK_END_HOUR = 22;

const DRAG_MIN_PX = 12;

/* ⚠️ MODULE-LEVEL, NOT `new Set()` IN THE DEFAULT. A fresh Set on every render is a
   new reference, which would defeat any memo downstream that depends on it and cost
   a re-render per frame during a drag. */
const EMPTY_APPT_IDS: ReadonlySet<string> = new Set<string>();
const SNAP_MINUTES = 15;

/**
 * ⛔⛤ ~~Short labels for the header counts~~ **DELETED 2026-09-18, decision 184.**
 * Its only reader was the hardcoded six-status header row, now replaced by
 * `<StatusFilterBar>`, which takes its labels from `APPOINTMENT_STATUSES` — the
 * one list (#254).
 *
 * ⛔ AND IT COULD NOT BE LEFT SITTING THERE: `web/tsconfig.json` sets
 * `"noUnusedLocals": true`, so an unused const FAILS THE BUILD. ⭐ Measured, not
 * assumed — the alternative was a green-looking edit that breaks `npm run build`.
 *
 *   ** A SIX-ENTRY MAP CALLED STATUS_LABEL, LEFT BESIDE A SEVEN-STATUS SCREEN, IS
 *      AN INVITATION TO REINTRODUCE THE BUG THAT WAS JUST REMOVED. **
 */

const STATUS_CLASSES: Record<AppointmentStatusCode, string> = {
    REQUESTED: 'bg-status-requested/85 border-status-requested text-white',
    APPROVED:  'bg-status-approved/85 border-status-approved text-white',
    ARRIVED:   'bg-status-arrived/85 border-status-arrived text-white',
    DEPARTED:  'bg-status-departed/70 border-status-departed text-white',
    NOSHOW:    'bg-status-noshow/85 border-status-noshow text-white',
    CANCELLED: 'bg-status-cancelled/40 border-status-cancelled text-white line-through',
};

// Block Time renders neutral/slate regardless of status — it's a door
// out-of-service window, not a truck.
const BLOCK_CLASS = 'bg-slate-500/80 border-slate-600 text-white';

// ---- Event types --------------------------------------------------------

export interface CreateInSlotEvent {
    /** null = the Unassigned queue. The create dialog's PrefillInfo has always
     *  read null as "unassigned bucket"; the day grid simply never sent it. */
    dockDoorId: number | null;
    startLocal: string;     // YYYY-MM-DDTHH:mm
    endLocal: string;
}

/** An arrival card dropped onto an existing appointment → attach the driver to it. */
export interface DropCheckInOnAppointmentEvent {
    checkInId: string;
    appt: AppointmentDto;
    /**
     * ⭐ The load this arrival is ALREADY on, or null when it is unmatched.
     * ⛔ Present means the drop is a MOVE and the old load ends up with no
     * arrival — the caller must ask before doing it.
     */
    fromAppointmentNumber?: string | null;
}

/**
 * A scan or an unfiled document dropped onto a load → file it there (#150).
 *
 * ⚠️ THE PAYLOAD, NOT A DOCUMENT ID. Half the things in the sliver are not documents
 * yet — a SharePoint scan has to be ingested first — and flattening that to an id here
 * would push the ingestion into the drag source, where a failure has nowhere to be
 * reported. The page decides; the grid just says what was dropped where.
 */
export interface DropPaperworkOnAppointmentEvent {
    payload: PaperworkDragPayload;
    appt: AppointmentDto;
}

/**
 * ⭐ A5 (2026-09-28) — a WMS order card dropped onto a load → attach it there.
 * ⚠️ The grid only says what was dropped where. The page asks the server where
 * the order is already attached and decides whether to show the replace popup.
 */
export interface DropWmsOrderOnAppointmentEvent {
    order: WmsOrderDragKey;
    /** ⭐ Only the id and number are read (F107): the Match screen's No WMS list hands
     *  over its own rows, which are not full AppointmentDtos. */
    appt: Pick<AppointmentDto, 'appointmentId' | 'appointmentNumber'>;
}

/** An arrival card dropped onto empty row space → create an appointment for it. */
export interface DropCheckInInSlotEvent extends CreateInSlotEvent {
    checkInId: string;
    /** ⛔ NARROWED BACK TO A REAL DOOR ON PURPOSE. A scheduler creating a queued
     *  placeholder is one thing; a driver who is physically at the site being
     *  dropped into a queue with no door is another, and the check-in flow has
     *  never offered it. Narrowing here keeps that path exactly as it was
     *  instead of widening it as a side effect of the create change. */
    dockDoorId: number;
}

/** Right-click on an empty part of a door row — the "block this door" entry. */
export interface RequestDoorMenuEvent {
    dockDoorId: number;
    doorCode: string;
    x: number;
    y: number;
    /** The hour the pointer was over, snapped — a sensible default block start. */
    startLocal: string;
    endLocal: string;
}

export interface MoveAppointmentEvent {
    appointmentId: number;
    /** The new door id. May be the same as the original (pure reschedule), or
     *  null when the appointment is unassigned (e.g. moved from the week grid,
     *  which has no door lanes). */
    dockDoorId: number | null;
    /** ISO UTC datetimes for the new window. Preserves duration. */
    newStartUtc: string;
    newEndUtc: string;
}

export interface RequestContextMenuEvent {
    appt: AppointmentDto;
    /** Screen coordinates for menu positioning */
    x: number;
    y: number;
}

export interface RequestLimitMenuEvent {
    /** The active limit band(s) in the clicked window (one per direction). */
    bands: { capacityId: number; direction: string; startMinutes: number; endMinutes: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
    x: number;
    y: number;
}

export interface RequestDedicationMenuEvent {
    dedicationId: number;
    label: string;
    startMinutes: number;
    endMinutes: number;
    effectiveStartDate: string | null;
    effectiveEndDate: string | null;
    x: number;
    y: number;
}

export interface EditLimitTimesEvent {
    /** All bands in the dragged window (one per direction), each its own rule. */
    bands: { capacityId: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
    startMinutes: number;
    endMinutes: number;
    /** Original window, so the handler can describe / confirm the change. */
    origStartMinutes: number;
    origEndMinutes: number;
}

export interface EditDedicationTimesEvent {
    dedicationId: number;
    label: string;
    effectiveStartDate: string | null;
    effectiveEndDate: string | null;
    startMinutes: number;
    endMinutes: number;
    origStartMinutes: number;
    origEndMinutes: number;
}

interface GanttCalendarProps {
    date: string;
    doors: CalendarDoorDto[];
    appointments: AppointmentDto[];
    /**
     * The hours to draw, as minutes since local midnight - the facility's
     * operating window for this weekday (CalendarDayDto.operatingWindow).
     * Omitted = the shipped 5 AM-10 PM.
     *
     * This is a STARTING POINT, NOT A CLIP. The grid widens it to cover
     * anything actually scheduled outside it, because an appointment the grid
     * cannot draw is invisible - and things do get booked outside hours: an
     * overtime truck, a maintenance block at 4 AM, a slot moved by a scheduler
     * who knows something the hours table doesn't.
     */
    operatingWindow?: { startMinutes: number; endMinutes: number; configured: boolean };
    /**
     * ⭐ F180 (Decision 297) — the facility's no-show grace in minutes: the possible
     * no-show mark waits this long after the slot ends (CalendarDayDto.noShowGraceMinutes;
     * the page passes 0 for an older backend, which is today's rule).
     */
    noShowGraceMinutes: number;
    /**
     * ---- ⭐⭐ THE STATUS FILTER, OWNED BY THE PAGE (decision 184, 2026-09-18) ----
     *
     * ⛔ REQUIRED, NOT OPTIONAL, AND THERE IS EXACTLY ONE CALL SITE TODAY. Making
     * them optional would let a second caller mount this grid with no filter and no
     * counts, and the header would silently render a bar that governs nothing.
     *
     *   ** A REQUIRED PROP IS A COMPILER-ENFORCED VERSION OF A RULE THAT WOULD
     *      OTHERWISE LIVE IN A COMMENT. **
     *
     * ⚠️ `statusCounts` / `totalCount` MUST BE COMPUTED OVER THE UNFILTERED DAY.
     * `appointments` above is already filtered — counting from it is the defect
     * this replaced.
     */
    statusFilter: Set<string>;
    toggleStatus: (code: string) => void;
    setStatusFilter: (next: Set<string>) => void;
    statusCounts: Record<string, number>;
    totalCount: number;
    /**
     * ---- ⭐⭐ THE SECOND FILTER AXIS (Pete, 2026-09-24) ----------------------
     *
     * > *"it would be great to filter the calendar by Unloading and Loading in the
     * > status bar"* → **"Separate control beside the status bar."**
     *
     * ⛔⛔ REQUIRED, NOT OPTIONAL, AND FOR THE REASON THIS BLOCK ALREADY GIVES
     * ABOUT `statusFilter`: Day draws its header HERE while Week and Month draw
     * `boardHeader` in `Calendar.tsx`. An optional prop left unpassed would put the
     * control on two views out of three, and a Day user filtered to Unloading would
     * have nothing on screen to turn it off.
     *   ** A REQUIRED PROP IS A COMPILER-ENFORCED VERSION OF A RULE THAT WOULD
     *      OTHERWISE LIVE IN A COMMENT. **
     *
     * ⚠️ `workCounts` CARRIES THE SAME OBLIGATION AS `statusCounts`: computed over
     * the UNFILTERED day. `appointments` above is already filtered.
     */
    workFilter: Set<string>;
    toggleWork: (code: string) => void;
    workCounts: Record<string, number>;
    /**
     * ---- ⭐⭐ THE THIRD FILTER AXIS (Pete, 2026-09-27) -----------------------
     *
     * > *"the WMS NO WMS search filters that follow Loading/Unloading next to
     * > status."*
     *
     * ⛔⛔ REQUIRED, NOT OPTIONAL, FOR THE REASON THE BLOCK ABOVE ALREADY GIVES
     * TWICE. Day draws its header HERE; Week and Month draw `boardHeader` in
     * `Calendar.tsx`. An optional prop left unpassed puts the control on two views
     * out of three, and a Day user filtered to NO WMS would have nothing on screen
     * to turn it off.
     *   ** A REQUIRED PROP IS A COMPILER-ENFORCED VERSION OF A RULE THAT WOULD
     *      OTHERWISE LIVE IN A COMMENT. **
     *
     * ⚠️ `wmsCounts` CARRIES THE SAME OBLIGATION AS THE OTHER TWO: computed over
     * the UNFILTERED day. `appointments` above is already filtered.
     */
    wmsFilter: Set<string>;
    toggleWms: (code: string) => void;
    wmsCounts: Record<string, number>;
    /**
     * ---- ⭐⭐ Ⓡ, BUILT BY THE PAGE AND MOUNTED HERE (Pete, 2026-09-27) --------
     *
     * > *"put the circle R to the right of XX Appointments"* → **"One Ⓡ in the board
     * > header, retire both."**
     *
     * ⛔⛔ A **NODE**, NOT `onReset` + `showReset`. Every other control on this strip
     * exists as duplicated markup in two files, and this file's own note says why
     * that costs: ⛔ DEDUPLICATING A COMPONENT DOES NOT DEDUPLICATE ITS SURROUNDINGS.
     * The two Ⓡ buttons this replaces had already drifted in SIZE — `h-4` in one file
     * and `h-6` in the other — and nobody could see it, because they were never on
     * screen together. Handing down the element makes that class of drift impossible
     * rather than merely discouraged.
     *   ** TWO SIZES FOR ONE CONTROL IS NOT A STYLE CHOICE, IT IS A DRIFT NOBODY CAN
     *      SEE BECAUSE THE TWO ARE NEVER ADJACENT. **
     *
     * ⚠️ OPTIONAL AND NULLABLE — the page passes `null` when nothing is scoped, so
     * this renders nothing rather than an empty slot that shifts the tally.
     */
    resetButton?: ReactNode;
    /** ⭐ Decision 266: the "Possible no-shows (N)" chip, built by the page so Day,
     *  Week and Month show the same element (the rule `resetButton` states above). */
    headerAlert?: ReactNode;
    /** Facility-wide appointment-limit bands for the viewed day (ruler strip). */
    /** ⭐ `perHour` (migration 188): the cap applies to EACH clock hour in the band. */
    /**
     * ---- ⛔⛤ `perHour?` WAS THE BUG (Pete, 2026-09-23) --------------------
     *
     * > *"I put this in as a per hour, shouldn't that state that somehow in the
     * > purple bar. Looks like now its 3 total available for the whole slot"*
     *
     * It WAS three for the whole slot, on screen. The flag is from migration 188
     * and it reached `evaluateFacilityCap`, so bookings were refused correctly --
     * but it was not SELECTed by the calendar query, not named in the DTO, and
     * declared OPTIONAL here. Three omissions, and the `?` is the one that made
     * them silent: it let a DTO with no such field satisfy this prop, so
     * `b.perHour` was `undefined` on every band and the whole per-hour feature
     * drew as its opposite.
     *   ** THE VALIDATOR AND THE PICTURE DISAGREED, AND ONLY THE PICTURE IS WHAT
     *      A SCHEDULER PLANS FROM. **
     * ⛔ REQUIRED NOW. If a future DTO drops it again, this file stops compiling.
     */
    capacityBands?: { capacityId: number; direction: string; startMinutes: number; endMinutes: number; maxConcurrent: number; perHour: boolean; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
    onSelectAppointment?: (appt: AppointmentDto) => void;
    onCreateInSlot?: (ev: CreateInSlotEvent) => void;
    onMoveAppointment?: (ev: MoveAppointmentEvent) => void;
    /** Right-click on empty row space. Page-level renders the menu. */
    onRequestDoorMenu?: (ev: RequestDoorMenuEvent) => void;
    onRequestContextMenu?: (ev: RequestContextMenuEvent) => void;
    /** Right-click a limit window (turn off / edit). */
    onRequestLimitMenu?: (ev: RequestLimitMenuEvent) => void;
    /** Click a limit window → open the Appointment Limits config screen. */
    onOpenLimits?: () => void;
    /** Drag a limit bar to resize/move it → commit new times for the whole rule. */
    onEditLimitTimes?: (ev: EditLimitTimesEvent) => void;
    /** Right-click a dedication band (make inactive / delete / edit). */
    onRequestDedicationMenu?: (ev: RequestDedicationMenuEvent) => void;
    /** Click a dedication band → open the Door Dedications config screen. */
    onOpenDedications?: () => void;
    /** Drag a dedication band to resize/move it → commit new times for the whole rule. */
    onEditDedicationTimes?: (ev: EditDedicationTimesEvent) => void;
    /** When provided, the door label becomes clickable and opens that door's editor. */
    onEditDoor?: (dockDoorId: number) => void;
    /**
     * Drop an unmatched arrival onto an appointment that has no driver yet.
     * ⚠️ Only appointments with NO driver accept a drop — see canAcceptCheckIn.
     * Attaching to an appointment that already has one would silently substitute
     * a driver, which is a decision for a human and a confirm dialog, not a drag.
     */
    onDropCheckInOnAppointment?: (ev: DropCheckInOnAppointmentEvent) => void;
    /** Drop an unmatched arrival onto empty row space → create an appointment there. */
    onDropCheckInInSlot?: (ev: DropCheckInInSlotEvent) => void;
    /**
     * Drop a scan or an unfiled document onto a load → file it there (#150).
     *
     * ⚠️ A DIFFERENT RULE FROM `onDropCheckInOnAppointment`. A load that already has a
     * truck still takes more paperwork, and a DEPARTED load takes the signed BOL that
     * comes back from the yard afterwards — see `paperworkRefusal`.
     */
    onDropPaperworkOnAppointment?: (ev: DropPaperworkOnAppointmentEvent) => void;
    /**
     * ⭐ A5 — drop a WMS order card onto a load → attach it (see `wmsOrderRefusal`).
     * ⚠️ OPTIONAL: without it an order drag is refused with a sentence, never ignored.
     */
    onDropWmsOrderOnAppointment?: (ev: DropWmsOrderOnAppointmentEvent) => void;
    /**
     * The appointment behind the arrival card the user is hovering in the
     * check-in panel. Outlined here so the two surfaces can be read as one.
     * ⚠️ An OUTLINE, not a colour change — colour already carries status.
     */
    highlightAppointmentId?: string | null;
    /**
     * ---- ⭐⭐ THE SECOND RING: WHAT IS **SELECTED** (decision 190, 2026-09-20) ----
     *
     * ⛔⛔ `highlightAppointmentId` IS A SINGLE VALUE AND CANNOT EXPRESS THIS. It
     * used to receive `hoverApptId ?? latchedApptId ?? focusApptId` — a ONE-RING
     * design by construction, where the hover REPLACED the latch and the latch came
     * back on mouse-out. That was right while hovering set the scope. Now that a
     * CLICK sets it, the two must be visible at once:
     *
     * > Pete, 2026-09-20: hovering a different load still outlines it *"so you can
     * > look around without losing your place."*
     *
     * ⭐ A **SET**, not an id — a client or carrier scope covers several loads, and
     * `RailFocus.appointmentIds` is already exactly that list.
     *
     * ⛔ AND THE TWO RINGS MUST NOT LOOK THE SAME. Same mark for "I chose this" and
     * "my cursor is here" would say two different things with one symbol; the
     * selection is full-strength and the hover is a lighter weight of it.
     *   ** A PROP THAT HOLDS ONE VALUE IS A CLAIM THAT ONLY ONE THING CAN BE TRUE. **
     */
    selectedAppointmentIds?: ReadonlySet<string>;
    /**
     * ---- ⭐⭐ THE HOVER, AS A SET — AND IT IS THE SAME OMISSION AGAIN ----------
     *
     * ⛔ Pete, 2026-09-21: *"When I hover a client, it is not highlighting the
     * appointments on the calendar its associated with."*
     *
     * The preview itself was built (Calendar.tsx `previewFocus`) and the RAIL
     * follows it correctly — the panels fill, the title fills. ⛔⛔ **But the board
     * could only ever mark ONE chip**, because `highlightAppointmentId` holds a
     * single id and a client hover covers several loads.
     *
     * ⭐ This is decision 190's own lesson, one prop later:
     *   ** A PROP THAT HOLDS ONE VALUE IS A CLAIM THAT ONLY ONE THING CAN BE
     *      TRUE. ** It was written about the SELECTION and the HOVER was left
     *      singular, so the same sentence had to be learned twice.
     *
     * ⚠️ SEPARATE FROM `selectedAppointmentIds`, NEVER MERGED INTO IT. Merging
     * would make a passing cursor indistinguishable from a deliberate choice,
     * which is the exact confusion decision 190 exists to prevent.
     *
     * ⛔ AND IT DOES NOT MOVE THE BOARD. S1AV.179's bound survives untouched: the
     * calendar FILTER never follows a hover. This marks; it does not reflow.
     *   ** A BAN ON MOVING THE BOARD IS NOT A BAN ON MARKING IT. **
     */
    previewAppointmentIds?: ReadonlySet<string>;
    /** Hovering a block here tells the panel which arrival to raise. */
    onHoverAppointment?: (appointmentId: string | null) => void;
    /**
     * ---- ⭐⭐ A SINGLE CLICK **SCOPES**; A DOUBLE CLICK **OPENS** (decision 190) ----
     *
     * ⛔ THIS REPLACES `onSelectAppointment` ON THE SINGLE CLICK, and the reason the
     * two are separate props rather than one with a flag is that they now fire from
     * different gestures with different costs: scoping is reversible and cheap,
     * opening is a screen.
     *
     * ⚠️ A DOUBLE-CLICK FIRES THE SINGLE FIRST, so opening a load also scopes to it.
     * Pete chose that over deferring the scope ~250ms: *a delay on the common path to
     * tidy up the rare one is the wrong trade.* The repeat is idempotent — the same
     * appointment scoped twice is the same scope.
     */
    onScopeAppointment?: (appt: AppointmentDto) => void;
    /**
     * ---- ⭐⭐ FIRES WITH THE HOVER CARD, NOT WITH THE CURSOR (2026-09-10) ----
     *
     * > Pete: *"can you fire it when the mousehover popup fires? So if the user
     * > is going really fast over appointments, it doesnt fire?"*
     *
     * ⛔ `onHoverAppointment` IS THE WRONG SIGNAL FOR THIS. It fires on
     * `mouseEnter`, immediately — so sweeping the cursor across a 23-door grid
     * on the way to something else would flip a panel dozens of times.
     *
     * ⭐ THIS ONE FIRES FROM INSIDE THE DWELL TIMER, at the same instant the
     * hover card appears, and clears when it goes. ⚠️ REUSING THE EXISTING
     * TIMING RATHER THAN INVENTING A SECOND ONE is the whole point: two
     * different "hovered long enough" delays on one grid would drift, and the
     * user would experience it as the panel and the tooltip disagreeing.
     */
    onHoverCardShown?: (appointmentId: string | null) => void;
    /** A drop that was refused, with the reason to show the user. */
    onDropRefused?: (reason: string) => void;
    /**
     * Appointment ids that ALREADY have a truck checked in against them.
     *
     * ⛔ ONE TRUCK = ONE APPOINTMENT. An appointment can have a check-in without
     * having a DRIVER row — attaching an arrival does not add to
     * AppointmentDriver — so `drivers.length` alone does not know an appointment
     * is taken. That gap is how two trucks ended up on A00000122.
     */
    claimedAppointmentIds?: ReadonlySet<string>;
    /** Facility SLOT MIN (minutes): resize snaps to multiples of this, minimum one. */
    slotMinutes?: number;
    /** Lazy loader for an appointment's event timeline (hover card). */
    fetchEvents?: (appointmentId: number) => Promise<AppointmentEventDto[]>;
    isToday?: boolean;
    /** ⭐ Which site's door order to remember — the key is per facility. */
    facilityCode: string;
}

// ---- Internal drag state -------------------------------------------------

interface CreateDrag {
    kind: 'create';
    /** null = the Unassigned queue row. */
    dockDoorId: number | null;
    startX: number;
    currentX: number;
}

interface MoveDrag {
    kind: 'move';
    appt: AppointmentDto;
    /** Original geometry — left/width within the door row */
    origLeft: number;
    origWidth: number;
    /** Original door row's vertical position (the row's top offset within the grid) */
    origRowTop: number;
    /** Pixel where the user pressed down — used to compute deltas */
    pointerStartX: number;
    pointerStartY: number;
    /** Current pixel positions */
    pointerX: number;
    pointerY: number;
}

type ActiveDrag = CreateDrag | MoveDrag | ResizeDrag;

interface ResizeDrag {
    kind: 'resize';
    appt: AppointmentDto;
    /** Which edge is being dragged: 'end' moves the end, 'start' moves the start. */
    edge: 'start' | 'end';
    /** Original block geometry (the opposite edge stays fixed). */
    origLeft: number;
    origWidth: number;
    pointerStartX: number;
    pointerX: number;
}

export function GanttCalendar({
    date,
    doors,
    appointments,
    statusFilter,
    toggleStatus,
    setStatusFilter,
    statusCounts,
    totalCount,
    workFilter,
    toggleWork,
    workCounts,
    wmsFilter,
    toggleWms,
    wmsCounts,
    resetButton,
    headerAlert,
    onSelectAppointment,
    onCreateInSlot,
    onMoveAppointment,
    onRequestDoorMenu,
    onRequestContextMenu,
    onRequestLimitMenu,
    onOpenLimits,
    onEditLimitTimes,
    onRequestDedicationMenu,
    onOpenDedications,
    onEditDedicationTimes,
    onEditDoor,
    onDropCheckInOnAppointment,
    onDropCheckInInSlot,
    onDropPaperworkOnAppointment,
    onDropWmsOrderOnAppointment,
    highlightAppointmentId = null,
    /* ⚠️ AN EMPTY SET, NOT `undefined`. Every read is a `.has()`, and a default of
       undefined would make each call site guard — which is how one of them ends up
       not guarding. */
    selectedAppointmentIds = EMPTY_APPT_IDS,
    previewAppointmentIds = EMPTY_APPT_IDS,
    onScopeAppointment,
    onHoverAppointment,
    onHoverCardShown,
    onDropRefused,
    claimedAppointmentIds,
    capacityBands = [],
    operatingWindow,
    noShowGraceMinutes,
    slotMinutes = 60,
    fetchEvents,
    isToday = false,
    facilityCode,
}: GanttCalendarProps) {
    // ---- Responsive hour column ------------------------------------------
    // Measured off the scroll viewport, not window.innerWidth: this grid lives
    // inside a page that has its own padding and could later sit beside a
    // sidebar, and the only number that matters is how much room THIS element
    // actually has. ResizeObserver so it tracks window resizes, zoom and the
    // browser devtools pane without a resize listener of our own.
    const scrollRef = useRef<HTMLDivElement>(null);
    const [viewportWidth, setViewportWidth] = useState(0);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setViewportWidth(entry.contentRect.width);
        });
        ro.observe(el);
        setViewportWidth(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    /**
     * THE DRAWN WINDOW = the facility's hours, WIDENED to swallow anything
     * scheduled outside them, then rounded out to whole hours so the axis
     * labels stay on the hour.
     *
     * The widening is the part that matters. Narrowing the grid to opening
     * hours is only safe if nothing can ever fall outside them, and things do:
     * an overtime truck, a 4 AM maintenance block, a dedication band that runs
     * to close. Clipping any of those would make real, editable rows invisible
     * with no indication they exist - strictly worse than the wide empty grid
     * this replaces. So the window can only ever GROW from the facility hours,
     * never shrink past them.
     */
    const { startHour, endHour, totalHours } = useMemo(() => {
        const cfgStart = operatingWindow ? operatingWindow.startMinutes / 60 : FALLBACK_START_HOUR;
        const cfgEnd   = operatingWindow ? operatingWindow.endMinutes   / 60 : FALLBACK_END_HOUR;
        let lo = Math.floor(cfgStart);
        let hi = Math.ceil(cfgEnd);

        const dayStart = parseISO(`${date}T00:00:00`);
        const swallow = (startMin: number, endMin: number) => {
            if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return;
            lo = Math.min(lo, Math.floor(startMin / 60));
            hi = Math.max(hi, Math.ceil(endMin / 60));
        };
        for (const a of appointments) {
            /**
             * ⛔ A DAY-ONLY APPOINTMENT MUST NOT WIDEN THE WINDOW. Its stored span
             * is the facility's whole operating window (and, read as UTC against a
             * local midnight, can reach past both ends of it) — so swallowing it
             * dragged the grid open to 12 AM and the packed chips landed there
             * instead of at the facility's start time. Pete, 2026-08-22: *"they need
             * to start at the facility start time 7:00am or the scheduler won't see
             * them."*
             *
             * ⚠️ THE FEEDBACK LOOP IS THE POINT: we stopped DRAWING these across the
             * day but left them MEASURING across it, so the window kept opening to
             * accommodate a span nothing renders. ⭐ Their chips are packed inside
             * the operating window by construction, so they can never need it wider.
             */
            // ⛔ A day-only truck with REAL times has a real span, so it must be
            // allowed to widen the drawn window like any other block — otherwise it
            // is drawn outside the grid and vanishes. Only the still-timeless ones
            // are skipped (their stored span is the whole operating window).
            if (isDayOnly(a) && !hasActualSpan(a)) continue;
            swallow(
                differenceInMinutes(parseISO(a.scheduledStartUtc), dayStart),
                differenceInMinutes(parseISO(a.scheduledEndUtc), dayStart),
            );
        }
        for (const b of capacityBands) swallow(b.startMinutes, b.endMinutes);
        for (const d of doors) for (const b of d.dedicationBands) swallow(b.startMinutes, b.endMinutes);

        // Clamp to the day and keep at least an hour so nothing divides by zero
        // on a facility whose hours are mid-edit.
        lo = Math.max(0, Math.min(lo, 23));
        hi = Math.min(24, Math.max(hi, lo + 1));
        return { startHour: lo, endHour: hi, totalHours: hi - lo };
    }, [operatingWindow, appointments, capacityBands, doors, date]);

    /**
      * ---- ⛔⛔ THESE FOUR LINES MOVED UP HERE, AND THE REASON IS A TDZ ----------
      *
      * `DOOR_LABEL_WIDTH_PX` is no longer a module constant — it depends on whether
      * the board is in the site's order, which depends on `manualOrder`. And
      * `hourWidth`, immediately below, subtracts that width from the viewport.
      * ⛔ A `const` declared after its reader is a ReferenceError at runtime, not a
      * type error, so this ordering is load-bearing rather than tidy.
      *
      * ⚠️ Moving `useState`/`useEffect` calls earlier is safe because they are
      * unconditional — the hook ORDER changes, and React only requires that it be
      * the SAME order on every render, which it is.
      */
    const [busyFirst, setBusyFirst] = useState(false);
    /**
     * ---- ⭐⭐ #76 / DECISION 213 — THE NAME SORT (Pete, 2026-09-22) -----------
     *
     * ⛔ THREE STATES, AND `null` IS THE IMPORTANT ONE. Off means *the order on
     * the board*, which is the site's order or the one Pete dragged — not
     * ascending. A two-state toggle would have no way to express "leave my
     * arrangement alone", and the arrangement is the thing he spent time on.
     *
     * ⚠️ IT IS NOT PERSISTED, deliberately, unlike `manualOrder`. A drag is a
     * decision about the board; a sort is a way of looking at it today.
     *   ** A VIEW TOGGLE'S OUTPUT WAS PERSISTED AS A PREFERENCE ONCE IN THIS FILE
     *      ALREADY, AND A PREFERENCE CANNOT BE TOGGLED OFF. ** (2026-09-19.)
     */
    const [doorSort, setDoorSort] = useState<DoorSortDir | null>(null);
    const [manualOrder, setManualOrder] = useState<number[] | null>(
        () => readDoorOrder(facilityCode));
    /* ⛔ RE-READ WHEN THE FACILITY CHANGES. The key is per facility, and a lazy
       initialiser runs ONCE — without this, switching to Denver would apply
       Brighton's arrangement to Denver's doors. ⚠️ The busy sort resets too: it is
       a view of THIS board, and carrying it across would be a claim about a day
       nobody has looked at yet. */
    useEffect(() => {
        setManualOrder(readDoorOrder(facilityCode));
        setBusyFirst(false);
        /* ⭐ #76 RESETS FOR THE SAME REASON THE PARTITION DOES: it is a view of
           THIS board, and carrying it across is a claim about a site nobody has
           looked at yet. ⛔ Omitting it here is how the 2026-09-22 stale-bands
           bug happened one component over — ** STALE STATE UNDER A NEW HEADING
           LOOKS LIKE A BUG IN THE ROWS. ** */
        setDoorSort(null);
    }, [facilityCode]);

    /* ⭐ The width follows the one conditional child. See the constants above. */
    const doorsAreInSiteOrder = isSiteOrder(doors, manualOrder);
    const DOOR_LABEL_WIDTH_PX = doorsAreInSiteOrder ? DOOR_LABEL_NARROW_PX : DOOR_LABEL_WIDE_PX;

    const hourWidth = useMemo(() => {
        // 0 on the very first paint, before the observer has reported. Fall back
        // to the floor rather than dividing by nothing and collapsing the grid.
        if (viewportWidth <= 0) return MIN_HOUR_WIDTH_PX;
        // Deliberately NOT rounded to a whole pixel: flooring left up to
        // totalHours px of dead space at the right edge (7px at 1920), which is
        // exactly the gap this change set out to remove. Every consumer of this
        // number already does float maths, and the snapping helpers round after
        // converting to minutes, so a fractional column costs nothing.
        const usable = viewportWidth - DOOR_LABEL_WIDTH_PX;
        return Math.max(MIN_HOUR_WIDTH_PX, usable / totalHours);
        /**
         * ---- ⛔⛔ `totalHours` IS IN HERE, AND LEAVING IT OUT WAS THE WHITE GAP --
         *
         * > Pete, 2026-09-10: *"Screen rendering for the calendar doesn't expand to
         * > the whole border. White space on the right after 5PM."*
         *
         * ⛔ THE DEPS WERE `[viewportWidth]` ALONE, so this memo froze the FIRST
         * render's `totalHours` — computed before the day's appointments and
         * operating window had loaded. When the real window arrived and the day got
         * SHORTER, `hourWidth` kept the value it had worked out for a longer day,
         * every column stayed too narrow, and the grid stopped short of its own
         * right edge. ⚠️ The comment above about not rounding is about ONE pixel;
         * this was losing hours of width.
         *
         * ⭐⭐ AND `react-hooks/exhaustive-deps` HAD ALREADY NAMED THIS LINE. It was
         * one of the eight violations I capped rather than read when wiring that
         * gate up for the hover bug — the same stale-closure shape, in the same
         * file, reported by the same rule, on the same afternoon. The cap is a
         * ratchet for old faults, not a place to leave a live one.
         */
    }, [viewportWidth, totalHours, DOOR_LABEL_WIDTH_PX]);
    const totalWidth = totalHours * hourWidth;

    const nowOffset = useMemo(() => {
        if (!isToday) return null;
        const now = new Date();
        const hours = now.getHours() + now.getMinutes() / 60;
        if (hours < startHour || hours > endHour) return null;
        return (hours - startHour) * hourWidth;
    }, [isToday, hourWidth, startHour, endHour]);

    // Facility-wide appointment-limit windows for the day, grouped by time span.
    // One neutral band per window spans all doors; the top marker shows an IN/OUT
    // badge per direction that has a cap, each with its own tooltip.
    const capWindows = useMemo(() => {
        /**
         * ---- ⭐⭐ TWO KINDS OF BAND, AND ONE OF THEM BECOMES MANY WINDOWS -----
         *
         * ⛔ Pete, 2026-09-22: *"we can't list out time because there will be too
         * many of them... I think you have to create a bar per hour though that
         * shows the indivdiual limit per hour."*
         *
         * A `perHour` band of 08:00-17:00 is NINE caps, not one, so it is expanded
         * here into nine windows. Everything downstream -- the marker bar, the
         * drag handles, the context menu -- then treats an hour exactly like any
         * other window and needed no changes.
         *   ** EXPANDING AT THE SOURCE COSTS ONE LOOP. EXPANDING AT EVERY READER
         *      COSTS EVERY READER, FOREVER. **
         *
         * ⚠️ WHOLE HOURS ONLY, and it is the SAME arithmetic as
         * `facilitySchedule.ts` and `FacilityHoursAdmin.tsx`: ceil(start/60) to
         * floor(end/60)-1. Three copies is a real cost, written down in all three.
         */
        type Win = {
            startMinutes: number; endMinutes: number;
            badges: { kind: 'IN' | 'OUT'; tip: string; blocked: boolean; label: string }[];
            blockedIn: boolean; blockedOut: boolean;
            perHour: boolean;
            bands: { capacityId: number; direction: string; startMinutes: number; endMinutes: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
        };
        const byWin = new Map<string, Win>();

        /**
         * ⚠️ THE COUNT IS DRAWN FROM WHAT THE BOARD IS SHOWING, and that is the
         * point: `2 of 3` must agree with the chips a scheduler can see, or they
         * will trust neither. ⛔ IT IS NOT THE AUTHORITY. `evaluateFacilityCap`
         * decides whether a booking is refused, it counts from the database, and
         * it can see loads this board is filtering out.
         *   ** A NUMBER ON A SCREEN AND A NUMBER IN A VALIDATOR ANSWER TO
         *      DIFFERENT MASTERS. SAY WHICH ONE REFUSES THE TRUCK. **
         */
        const dayStartLocal = parseISO(`${date}T00:00:00`);
        const spans = appointments
            .filter(a => a.direction !== 'BLOCK')
            .map(a => ({
                direction: a.direction,
                start: differenceInMinutes(parseISO(a.scheduledStartUtc), dayStartLocal),
                end:   differenceInMinutes(parseISO(a.scheduledEndUtc), dayStartLocal),
            }));
        const usedIn  = (s0: number, e0: number) =>
            spans.filter(x => x.direction === 'INBOUND'  && x.start < e0 && x.end > s0).length;
        const usedOut = (s0: number, e0: number) =>
            spans.filter(x => x.direction === 'OUTBOUND' && x.start < e0 && x.end > s0).length;

        const addWindow = (
            b: { capacityId: number; direction: string; startMinutes: number; endMinutes: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null },
            winStart: number, winEnd: number, perHour: boolean,
        ) => {
            const key = `${winStart}-${winEnd}`;
            const e = byWin.get(key) ?? {
                startMinutes: winStart, endMinutes: winEnd,
                badges: [], blockedIn: false, blockedOut: false, perHour, bands: [],
            };
            e.bands.push(b);
            const blocked = b.maxConcurrent === 0;
            const unit = perHour ? 'in this hour' : 'at once';
            /* ⭐ `2 of 3`, not `3`. A cap alone tells a scheduler what the rule is;
               the pair tells them whether they can book. */
            const mk = (kind: 'IN' | 'OUT', used: number, tip: string) => ({
                kind, tip, blocked, label: blocked ? kind : `${kind} ${used}/${b.maxConcurrent}`,
            });
            if (b.direction === 'INBOUND') {
                e.badges.push(mk('IN', usedIn(winStart, winEnd),
                    blocked ? 'Inbound blocked (limit 0)' : `Inbound limit: ${b.maxConcurrent} ${unit}`));
                if (blocked) e.blockedIn = true;
            } else if (b.direction === 'OUTBOUND') {
                e.badges.push(mk('OUT', usedOut(winStart, winEnd),
                    blocked ? 'Outbound blocked (limit 0)' : `Outbound limit: ${b.maxConcurrent} ${unit}`));
                if (blocked) e.blockedOut = true;
            } else {
                /* ⛔ A 'BOTH' BAND IS ONE POOL, NOT TWO. The tooltip says combined
                   and the count is the sum, because that is what the validator
                   does -- `scope = inCount + outCount` for a BOTH row. */
                const both = usedIn(winStart, winEnd) + usedOut(winStart, winEnd);
                const tip = blocked ? 'In + out blocked (limit 0)' : `Combined in + out limit: ${b.maxConcurrent} ${unit}`;
                e.badges.push(mk('IN', both, tip));
                e.badges.push(mk('OUT', both, tip));
                if (blocked) { e.blockedIn = true; e.blockedOut = true; }
            }
            byWin.set(key, e);
        };

        for (const b of capacityBands) {
            if (b.perHour) {
                const first = Math.ceil(b.startMinutes / 60);
                const last  = Math.floor(b.endMinutes / 60) - 1;
                for (let h = first; h <= last; h++) addWindow(b, h * 60, h * 60 + 60, true);
            } else {
                addWindow(b, b.startMinutes, b.endMinutes, false);
            }
        }

        // Keep at most one IN and one OUT badge per window, preferring a blocked one.
        for (const e of byWin.values()) {
            const seen = new Set<string>();
            e.badges.sort((a, b) => Number(b.blocked) - Number(a.blocked));
            e.badges = e.badges.filter(bd => (seen.has(bd.kind) ? false : (seen.add(bd.kind), true)));
        }
        return Array.from(byWin.values());
    }, [capacityBands, appointments, date]);

    // The pinned limit marker bar sits in a reserved strip just under the time
    // axis; door rows start below it so appointments never render under the bar.
    const CAP_MARKER_H = 20;
    const capOffset = capWindows.length > 0 ? CAP_MARKER_H : 0;

    const UNASSIGNED_DOOR_ID = 0; // sentinel: real dockDoorIds are always > 0

    // ---- Check-in drag-and-drop ------------------------------------------
    // Highlighted target, so the supervisor can see where the driver will land
    // BEFORE letting go. Without this the whole gesture is a guess.
    const [dropHover, setDropHover] = useState<
        { kind: 'appt'; appointmentId: number }
        | { kind: 'reject'; appointmentId: number }
        // ⭐ #150. Its own kind, and its own colour, because it is a different
        // outcome: emerald says "this truck attaches here", sky says "this paperwork
        // files here". A shared highlight would make the two gestures look identical
        // at the exact moment the scheduler is deciding to let go.
        | { kind: 'paperwork'; appointmentId: number }
        // ⭐ A5 — violet: attaching an order is neither a truck nor a document.
        | { kind: 'order'; appointmentId: number }
        | { kind: 'row'; dockDoorId: number }
        | null
    >(null);

    /**
     * Is this a check-in being dragged? dragover cannot READ the payload (the
     * browser withholds it until drop, deliberately), so the MIME type is the
     * only thing available to decide whether to accept.
     */
    const isCheckInDrag = (e: ReactDragEvent) =>
        e.dataTransfer.types.includes(CHECKIN_DND_MIME);

    /**
     * ⭐ AND IS THIS A DOCUMENT? (#150) Two different drags end on the same chip and
     * mean different things — attach a truck, or file a bill of lading — so each is
     * recognised by its own MIME type and handled separately. A single handler that
     * inferred the meaning from what happened to be on the clipboard would eventually
     * attach a truck because somebody dragged a scan.
     */
    const isPaperworkDragEvent = (e: ReactDragEvent) => isPaperworkDrag(e.dataTransfer);

    /**
     * ⚠️ ALMOST ANYTHING TAKES PAPERWORK, AND THAT IS NOT THE SAME RULE AS A DRIVER.
     *
     * A load that already has a truck on it still takes another document — usually
     * several. A DEPARTED load takes one too: the signed BOL comes back from the yard
     * after the truck has gone, which is the single most common late-paperwork case
     * there is, and refusing it would send the scheduler to a dialog to do the same
     * thing.
     *
     * ⛔ A BLOCK IS THE EXCEPTION. It is a door outage, not a load — there is nothing
     * for a document to be about, and `AppointmentId` on a block would put paperwork
     * somewhere no report reads.
     */
    const paperworkRefusal = (appt: AppointmentDto): string | null => {
        if (!onDropPaperworkOnAppointment) return 'Filing is not available here.';
        if (appt.direction === 'BLOCK') return 'That is a blocked door, not an appointment.';
        if (appt.statusCode === 'CANCELLED') {
            return `${appt.appointmentNumber} is cancelled — file it on the appointment that ran.`;
        }
        return null;
    };

    /**
     * ⭐ A5 — WHICH LOADS TAKE AN ORDER. A block holds a door and carries nothing
     * (the server refuses it too, 409). A cancelled load will never run, so an
     * order put there would read as covered when it is not.
     * ⚠️ A DEPARTED load DOES take one: reconciling the paperwork after the truck
     * has gone is exactly when an order gets attached by hand.
     */
    const wmsOrderRefusal = (appt: AppointmentDto): string | null => {
        if (!onDropWmsOrderOnAppointment) return 'Attaching orders is not available here.';
        if (appt.direction === 'BLOCK') return 'That is a blocked door, not an appointment.';
        if (appt.statusCode === 'CANCELLED') {
            return `${appt.appointmentNumber} is cancelled — attach the order to the appointment that will run.`;
        }
        return null;
    };

    /**
     * ⚠️ WHICH APPOINTMENTS ACCEPT A DRIVER.
     *
     * Pete's rule: drag onto "an existing appointment that doesn't have a driver
     * assigned". So an appointment that already has one is NOT a target -- the
     * substitute-driver case goes through the panel's Attach button, which asks
     * first. A drag is too easy to do by accident to carry that meaning.
     *
     * BLOCKs are door outages, not trucks. Finished appointments cannot receive
     * an arrival that is still on site.
     */
    /**
     * ⚠️ RETURNS THE REASON, NOT A BOOLEAN. A refused target used to be simply
     * inert — no cursor change, no message — which is indistinguishable from the
     * feature being broken. The scheduler needs to know WHY, and "why" is
     * different for a blocked door and an appointment that already has a driver.
     */
    const checkInRefusal = (appt: AppointmentDto): string | null => {
        if (!onDropCheckInOnAppointment) return 'Attaching is not available here.';
        if (appt.direction === 'BLOCK') return 'That is a blocked door, not an appointment.';
        if (appt.statusCode === 'CANCELLED') return `${appt.appointmentNumber} is cancelled.`;
        if (appt.statusCode === 'NOSHOW') return `${appt.appointmentNumber} is marked no-show.`;
        if (appt.statusCode === 'DEPARTED') return `${appt.appointmentNumber} has already departed.`;
        if (claimedAppointmentIds?.has(String(appt.appointmentId))) {
            return `${appt.appointmentNumber} already has a truck checked in — `
                 + 'one truck is one appointment. Create a new appointment for this driver.';
        }
        if (appt.drivers.length > 0) {
            const who = appt.drivers.map(d => d.name).filter(Boolean).join(', ');
            return `${appt.appointmentNumber} already has ${who || 'a driver'} — `
                 + 'use Attach on the card to swap drivers.';
        }
        return null;
    };
    const canAcceptCheckIn = (appt: AppointmentDto): boolean => checkInRefusal(appt) === null;

    /**
     * ---- ⭐⭐ THE DOOR ROW ORDER (2026-09-10) ------------------------------
     *
     * ⛔ A SNAPSHOT ON A CLICK, NEVER A LIVE SORT — Pete: *"It doesn't run
     * automatically, its a view button."* A door taking its first booking at 10am
     * must not make its row jump while somebody is dragging onto it. The whole
     * rule set is in `lib/doorOrder.ts`.
     */

    /* ⚠️ Which row is being dragged, and which one the cursor is over. `null` when
       no row drag is in flight — the appointment drags are unaffected. */
    const [rowDragId, setRowDragId] = useState<number | null>(null);
    const [rowOverId, setRowOverId] = useState<number | null>(null);
    /* ⛔ A DRAG MUST NOT ALSO OPEN THE DOOR EDITOR. The label cell is clickable;
       without this, every reorder would end with the edit dialog on screen. */
    const rowDraggedRef = useRef(false);

    const apptsByDoor = useMemo(() => {
        const map = new Map<number, AppointmentDto[]>();
        for (const a of appointments) {
            // Door-less (queued) appointments go in the synthetic Unassigned
            // lane (key 0) instead of being dropped.
            const key = a.dockDoorId ?? UNASSIGNED_DOOR_ID;
            const arr = map.get(key) ?? [];
            arr.push(a);
            map.set(key, arr);
        }
        return map;
    }, [appointments]);

    /* ⛔ ONE PREDICATE, THREE READERS: the row order, the drop, and the dragover
       that decides whether the drop is even legal. Three copies of
       `(apptsByDoor.get(id)?.length ?? 0) > 0` is three chances for the cursor to
       promise something the drop then refuses. */
    const hasAppointments = useCallback(
        (d: { dockDoorId: number }) => (apptsByDoor.get(d.dockDoorId)?.length ?? 0) > 0,
        [apptsByDoor]);

    const rows = useMemo(() => {
        // ⚠️ ALWAYS RENDERED, EVEN WHEN EMPTY. It used to appear only when
        // something was in it, which meant the one place a scheduler looks for
        // trucks with no door was invisible exactly when he was checking whether
        // there were any -- and it is also the drop target for an arrival that
        // has no door yet, so a disappearing row is a disappearing target.
        const queueDoor: CalendarDoorDto = {
            dockDoorId: UNASSIGNED_DOOR_ID,
            code: 'QUEUE',
            name: 'Unassigned',
            doorGroup: null,
            displayOrder: -1,
            maxConcurrent: 99,
            allowedDirection: 'BOTH',
            capabilities: [],
            dedicatedTo: null,
            dedicationBands: [],
            dedications: [],
        };
        /**
         * ---- ⭐⭐ THE QUEUE IS PREPENDED **AFTER** THE SORT (2026-09-10) -----
         *
         * > Pete: *"re-arrange the Dock Door Rows (not unassigned)"*.
         *
         * ⛔ `orderDoors` NEVER SEES THE QUEUE ROW. It is not a door — it is the
         * drop target for a truck that has none, and it is where a scheduler looks
         * to find out whether there are any. ⚠️ Sorting it with the rest would bury
         * it under twenty-three doors the first time every door was busy, and the
         * symptom would be "the Unassigned row is gone".
         */
        return [queueDoor, ...orderDoors(
            doors, manualOrder, busyFirst,
            hasAppointments,
            /* ⭐ #76. `nameOf` is the LABEL THE ROW ACTUALLY DRAWS — `door.name ||
               door.code`, the same expression the cell uses below. Sorting on a
               field the scheduler cannot see is a sort that looks broken.
               ⚠️ The queue row is prepended AFTER this call and is never sorted,
               in either direction: it is not a door. */
            doorSort ? { dir: doorSort, nameOf: (d) => d.name || d.code } : null,
        )];
    }, [doors, apptsByDoor, hasAppointments, manualOrder, busyFirst, doorSort]);

    // ---- Layout model -----------------------------------------------------
    // For each rendered row (door), compute its lane assignment, pixel height
    // (laneCount * LANE_HEIGHT_PX), and cumulative top offset within the grid.
    // Everything that positions rows/blocks/ghost/drag-lookup reads from here,
    // so variable row heights stay consistent across all of them.
    const layout = useMemo(() => {
        const lanesByDoor = new Map<number, Map<number, number>>(); // doorId -> (apptId -> lane)
        const spansByDoor = new Map<number, Map<number, EffectiveSpan>>(); // doorId -> (apptId -> span)
        const heightByDoor = new Map<number, number>();             // doorId -> px
        const topByDoor = new Map<number, number>();                // doorId -> px (cumulative, excl. header)
        let cursor = 0;
        for (const door of rows) {
            const appts = apptsByDoor.get(door.dockDoorId) ?? [];
            /**
             * ⚠️ PACKED PER DOOR ROW, not across the whole grid. Each row lays its
             * own day-only chips out from the left; two rows both starting at the
             * window edge is correct, because a chip's x means "nth untimed truck
             * in this row", not a time.
             */
            const spans = effectiveSpans(appts, date, startHour, operatingWindow);
            spansByDoor.set(door.dockDoorId, spans);
            const { laneOf, laneCount } = assignLanes(appts, spans);
            const h = laneCount * LANE_HEIGHT_PX;
            lanesByDoor.set(door.dockDoorId, laneOf);
            heightByDoor.set(door.dockDoorId, h);
            topByDoor.set(door.dockDoorId, cursor);
            cursor += h;
        }
        return { lanesByDoor, spansByDoor, heightByDoor, topByDoor, totalHeight: cursor };
    }, [rows, apptsByDoor, date, startHour, operatingWindow]);

    /** ⭐ One flat lookup, so every computeBlockGeometry call reads the same spans
     *  the lanes were built from. Keyed by appointment, since ids are unique. */
    const spanOf = useMemo(() => {
        const all = new Map<number, EffectiveSpan>();
        for (const m of layout.spansByDoor.values()) for (const [k, v] of m) all.set(k, v);
        return all;
    }, [layout]);

    // Map a Y offset (already relative to the grid body, header excluded, scroll
    // added) to the door row it falls in — walks variable row heights.
    const doorIdAtY = useCallback(
        (yInGridBody: number): number | null => {
            let acc = 0;
            for (const door of rows) {
                const h = layout.heightByDoor.get(door.dockDoorId) ?? LANE_HEIGHT_PX;
                if (yInGridBody >= acc && yInGridBody < acc + h) return door.dockDoorId;
                acc += h;
            }
            return null;
        },
        [rows, layout],
    );

    // Initial scroll-to-now. (scrollRef is declared at the top of the component
    // because the responsive hour-width measurement needs it before this point.)
    useEffect(() => {
        if (nowOffset !== null && scrollRef.current) {
            const target = Math.max(0, nowOffset - 200);
            scrollRef.current.scrollLeft = target;
        }
    }, [nowOffset]);

    // ---- Active drag state (either create or move) ----
    const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

    // ---- Hover timeline card ----
    const [hover, setHover] = useState<{ appt: AppointmentDto; x: number; y: number } | null>(null);
    // X (px, within the grid container) of a transient hover guide line, snapped
    // to the nearest 15-min tick. Null when the pointer isn't over the time area.
    const [hoverGuideLeft, setHoverGuideLeft] = useState<number | null>(null);
    const [hoverEvents, setHoverEvents] = useState<AppointmentEventDto[] | 'loading' | { error: string } | null>(null);
    const hoverTimer = useRef<number | null>(null);
    const hoveredIdRef = useRef<number | null>(null);
    const eventsCache = useRef<Map<number, AppointmentEventDto[]>>(new Map());

    // ---- Limit-bar drag (resize/move) — isolated from the appointment drag ----
    type CapDrag = {
        mode: 'move' | 'resize-l' | 'resize-r';
        bands: { capacityId: number; maxConcurrent: number; effectiveStartDate: string | null; effectiveEndDate: string | null }[];
        origStart: number; origEnd: number; startClientX: number;
    };
    const [capDrag, setCapDrag] = useState<CapDrag | null>(null);
    const [capPreview, setCapPreview] = useState<{ start: number; end: number } | null>(null);
    const capMovedRef = useRef(false);

    useEffect(() => {
        if (!capDrag) return;
        const GRID_MIN = startHour * 60, GRID_MAX = endHour * 60;
        const pxPerMin = hourWidth / 60;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        const onMove = (e: MouseEvent) => {
            const delta = Math.round(((e.clientX - capDrag.startClientX) / pxPerMin) / SNAP_MINUTES) * SNAP_MINUTES;
            let ns = capDrag.origStart, ne = capDrag.origEnd;
            if (capDrag.mode === 'move') {
                const dur = capDrag.origEnd - capDrag.origStart;
                ns = clamp(capDrag.origStart + delta, GRID_MIN, GRID_MAX - dur); ne = ns + dur;
            } else if (capDrag.mode === 'resize-l') {
                ns = clamp(capDrag.origStart + delta, GRID_MIN, capDrag.origEnd - SNAP_MINUTES);
            } else {
                ne = clamp(capDrag.origEnd + delta, capDrag.origStart + SNAP_MINUTES, GRID_MAX);
            }
            if (ns !== capDrag.origStart || ne !== capDrag.origEnd) capMovedRef.current = true;
            setCapPreview({ start: ns, end: ne });
        };
        const onUp = () => {
            setCapPreview(prev => {
                if (prev && capMovedRef.current && (prev.start !== capDrag.origStart || prev.end !== capDrag.origEnd)) {
                    onEditLimitTimes?.({
                        bands: capDrag.bands,
                        startMinutes: prev.start, endMinutes: prev.end,
                        origStartMinutes: capDrag.origStart, origEndMinutes: capDrag.origEnd,
                    });
                }
                return null;
            });
            setCapDrag(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [capDrag, onEditLimitTimes, hourWidth, startHour, endHour]);

    // ---- Dedication-band drag (resize/move) — mirrors the limit-bar drag ----
    type DedDrag = {
        mode: 'move' | 'resize-l' | 'resize-r';
        dedicationId: number; label: string;
        effectiveStartDate: string | null; effectiveEndDate: string | null;
        origStart: number; origEnd: number; startClientX: number;
    };
    const [dedDrag, setDedDrag] = useState<DedDrag | null>(null);
    const [dedPreview, setDedPreview] = useState<{ start: number; end: number } | null>(null);
    const dedMovedRef = useRef(false);

    useEffect(() => {
        if (!dedDrag) return;
        const GRID_MIN = startHour * 60, GRID_MAX = endHour * 60;
        const pxPerMin = hourWidth / 60;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        const onMove = (e: MouseEvent) => {
            const delta = Math.round(((e.clientX - dedDrag.startClientX) / pxPerMin) / SNAP_MINUTES) * SNAP_MINUTES;
            let ns = dedDrag.origStart, ne = dedDrag.origEnd;
            if (dedDrag.mode === 'move') {
                const dur = dedDrag.origEnd - dedDrag.origStart;
                ns = clamp(dedDrag.origStart + delta, GRID_MIN, GRID_MAX - dur); ne = ns + dur;
            } else if (dedDrag.mode === 'resize-l') {
                ns = clamp(dedDrag.origStart + delta, GRID_MIN, dedDrag.origEnd - SNAP_MINUTES);
            } else {
                ne = clamp(dedDrag.origEnd + delta, dedDrag.origStart + SNAP_MINUTES, GRID_MAX);
            }
            if (ns !== dedDrag.origStart || ne !== dedDrag.origEnd) dedMovedRef.current = true;
            setDedPreview({ start: ns, end: ne });
        };
        const onUp = () => {
            setDedPreview(prev => {
                if (prev && dedMovedRef.current && (prev.start !== dedDrag.origStart || prev.end !== dedDrag.origEnd)) {
                    onEditDedicationTimes?.({
                        dedicationId: dedDrag.dedicationId, label: dedDrag.label,
                        effectiveStartDate: dedDrag.effectiveStartDate, effectiveEndDate: dedDrag.effectiveEndDate,
                        startMinutes: prev.start, endMinutes: prev.end,
                        origStartMinutes: dedDrag.origStart, origEndMinutes: dedDrag.origEnd,
                    });
                }
                return null;
            });
            setDedDrag(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [dedDrag, onEditDedicationTimes, hourWidth, startHour, endHour]);

    const clearHoverTimer = () => {
        if (hoverTimer.current !== null) { window.clearTimeout(hoverTimer.current); hoverTimer.current = null; }
    };

    const handleApptMouseEnter = useCallback(
        (e: React.MouseEvent, appt: AppointmentDto) => {
            if (!fetchEvents) return;
            if (activeDrag) return;                // no hover card mid-drag
            /**
             * ⛔⛔ AND NO TRAY SWITCH MID-PAPERWORK-DRAG, WHICH IS A DIFFERENT DRAG.
             *
             * `activeDrag` is the GRID's own gesture — moving an appointment. A scan
             * dragged out of the tray is an HTML5 drag this component never sees the
             * start of, so it passed straight through this guard, the dwell fired,
             * the tray swapped mode and UNMOUNTED THE ROW BEING DRAGGED. See
             * `paperworkDragInProgress`.
             */
            if (paperworkDragInProgress()) return;
            /* ⛔ SAME FOR AN ORDER DRAG (A5): the rail holding the dragged row is
               the one a hover would re-scope. */
            if (wmsOrderDragInProgress()) return;
            clearHoverTimer();
            hoveredIdRef.current = appt.appointmentId;
            const x = e.clientX, y = e.clientY;
            hoverTimer.current = window.setTimeout(async () => {
                setHover({ appt, x, y });
                /* ⭐ THE DWELL HAS PASSED — this is the moment the card appears,
                   and the only moment a panel should follow the cursor. */
                onHoverCardShown?.(String(appt.appointmentId));
                // Show whatever we have instantly (no loading flash), but ALWAYS
                // refetch so newly-logged events (status changes, emails) appear.
                const cached = eventsCache.current.get(appt.appointmentId);
                setHoverEvents(cached ?? 'loading');
                try {
                    const evs = await fetchEvents(appt.appointmentId);
                    eventsCache.current.set(appt.appointmentId, evs);
                    if (hoveredIdRef.current === appt.appointmentId) setHoverEvents(evs);
                } catch (err) {
                    // If a refresh fails but we had cached data, keep showing it
                    // rather than flashing an error.
                    if (hoveredIdRef.current === appt.appointmentId && !cached) {
                        setHoverEvents({ error: err instanceof Error ? err.message : 'request failed' });
                    }
                }
            }, 350);
        },
        /**
         * ---- ⛔⛔ `onHoverCardShown` IS IN HERE, AND LEAVING IT OUT WAS THE BUG --
         *
         * > Pete, 2026-09-10, third report: *"the hover on the appointment is still
         * > not working, only on the driver."*
         *
         * ⛔ THE DEPS WERE `[fetchEvents, activeDrag]`. This callback therefore
         * froze the FIRST render's `onHoverCardShown` — and on that render
         * `Calendar`'s `showAttachedFor` closed over `calendar === null`, because
         * the day had not loaded yet. So every appointment hover for the rest of
         * the session ran a handler that could not find ANY appointment, took the
         * "not a load" branch, and called `setAttachedPaperwork(null)`.
         *
         * ⚠️ IT DID NOT FAIL. IT CLEARED. Hovering a chip actively wiped a view the
         * driver rail had just put up — which is why it read as "appointment hover
         * does nothing" rather than as an error.
         *
         * ⭐ AND IT IS WHY THE DRIVER RAIL WORKED. `CheckInPanel`'s equivalent
         * callback lists `[onHoverCardShown, clearTrayDwell]`, so it always held
         * the current one. Two call sites, one dependency array wrong: the same
         * feature working in one place and silently inert in the other.
         *
         * ⛔⛔ `react-hooks/exhaustive-deps` REPORTS THIS EXACT LINE, by name, and
         * always did. The deploy runs `rules-of-hooks` only. See the note in
         * `deploy-frontend-print.ps1` — the rule that catches this is now run.
         */
        [fetchEvents, activeDrag, onHoverCardShown],
    );

    const handleApptMouseLeave = useCallback(() => {
        clearHoverTimer();
        hoveredIdRef.current = null;
        setHover(null);
        setHoverEvents(null);
        /**
         * ---- ⛔⛔ ~~onHoverCardShown?.(null)~~ REMOVED (Pete, 2026-09-10) -------
         *
         * > *"when you hover over driver, scanned paperwork shows up but doesn't
         * > stay, no point. Needs to stay until next hover or the user toggles."*
         *
         * ⛔ AND IT IS ALSO WHY APPOINTMENT HOVER LOOKED COMPLETELY DEAD. The
         * hover card opens AT THE CURSOR, over the chip — so the chip's own
         * mouseleave fired within a frame of the dwell elapsing, and this line
         * undid the switch before it could be seen. On a driver card there is no
         * popup stealing the pointer, which is exactly why Pete saw the tray
         * change there and not here. ⚠️ ONE LINE, TWO SYMPTOMS THAT LOOKED LIKE
         * DIFFERENT BUGS.
         *
         * ⭐ THE TRAY IS NOW A MODE, NOT A TOOLTIP. It holds what it was last
         * pointed at until the next dwell replaces it or the person toggles back
         * — the panel is renamed "Attached Paperwork" and carries that toggle, so
         * there is no state here a scheduler cannot see or leave.
         */
    }, []);

    useEffect(() => () => clearHoverTimer(), []);

    /**
     * Window-level mouse listeners while ANY drag is active. We attach to
     * window so the drag survives even if the cursor leaves the grid.
     */
    useEffect(() => {
        if (!activeDrag) return;

        const onMove = (e: MouseEvent) => {
            if (activeDrag.kind === 'create') {
                const rowEl = document.querySelector(
                    `[data-row-grid="${activeDrag.dockDoorId}"]`,
                ) as HTMLElement | null;
                if (!rowEl) return;
                const rect = rowEl.getBoundingClientRect();
                const x = Math.max(0, Math.min(totalWidth, e.clientX - rect.left));
                setActiveDrag((d) => (d && d.kind === 'create' ? { ...d, currentX: x } : d));
            } else if (activeDrag.kind === 'move') {
                setActiveDrag((d) =>
                    d && d.kind === 'move' ? { ...d, pointerX: e.clientX, pointerY: e.clientY } : d,
                );
            } else if (activeDrag.kind === 'resize') {
                setActiveDrag((d) =>
                    d && d.kind === 'resize' ? { ...d, pointerX: e.clientX } : d,
                );
            }
        };

        const onUp = (_e: MouseEvent) => {
            setActiveDrag((d) => {
                if (!d) return null;

                if (d.kind === 'create') {
                    if (!onCreateInSlot) return null;
                    const dragPx = Math.abs(d.currentX - d.startX);
                    if (dragPx < DRAG_MIN_PX) return null;
                    const fromX = Math.min(d.startX, d.currentX);
                    const toX = Math.max(d.startX, d.currentX);
                    onCreateInSlot({
                        dockDoorId: d.dockDoorId,
                        startLocal: pxToLocalDateTimeString(fromX, date, hourWidth, startHour),
                        endLocal: pxToLocalDateTimeString(toX, date, hourWidth, startHour),
                    });
                    return null;
                }

                if (d.kind === 'resize') {
                    if (!onMoveAppointment) return null;
                    const deltaX = d.pointerX - d.pointerStartX;
                    if (Math.abs(deltaX) < DRAG_MIN_PX) return null;  // negligible → no-op
                    // The opposite edge stays fixed. Normal appointments snap to
                    // whole SLOT MIN multiples; blocks aren't slot-bound, so they
                    // snap only to the fine calendar grid (SNAP_MINUTES).
                    const newWidthPx = d.edge === 'end'
                        ? d.origWidth + deltaX     // end moves
                        : d.origWidth - deltaX;    // start moves (end fixed)
                    const rawDurationMin = (newWidthPx / hourWidth) * 60;
                    const resizeStep = d.appt.direction === 'BLOCK' ? SNAP_MINUTES : slotMinutes;
                    const snappedDuration = Math.max(
                        resizeStep,
                        Math.round(rawDurationMin / resizeStep) * resizeStep,
                    );
                    const startUtc = parseISO(d.appt.scheduledStartUtc);
                    const endUtc   = parseISO(d.appt.scheduledEndUtc);
                    let newStartUtc: string;
                    let newEndUtc: string;
                    if (d.edge === 'end') {
                        newStartUtc = d.appt.scheduledStartUtc;  // start fixed
                        newEndUtc = new Date(startUtc.getTime() + snappedDuration * 60_000).toISOString();
                    } else {
                        newEndUtc = d.appt.scheduledEndUtc;      // end fixed
                        newStartUtc = new Date(endUtc.getTime() - snappedDuration * 60_000).toISOString();
                    }
                    onMoveAppointment({
                        appointmentId: d.appt.appointmentId,
                        dockDoorId: d.appt.dockDoorId!,           // resize only offered on doored appts
                        newStartUtc,
                        newEndUtc,
                    });
                    return null;
                }

                // d.kind === 'move'
                if (!onMoveAppointment) return null;
                const deltaX = d.pointerX - d.pointerStartX;
                const deltaY = d.pointerY - d.pointerStartY;
                /* ⛔ A SHORT DRAG IS A CLICK, AND A CLICK NOW **SCOPES** (decision 190).
                   ⚠️ THE CHIP HAS NO `onClick` AND NEVER HAS — the click is synthesised
                   here, from a press-drag-release that moved less than DRAG_MIN_PX,
                   because the same gesture at a greater distance reschedules the appointment.
                   Wiring a real `onClick` would fire on BOTH and scope every reschedule.
                     ** ON A DRAG HANDLE, THE CLICK IS A MEASUREMENT, NOT AN EVENT. ** */
                if (Math.abs(deltaX) < DRAG_MIN_PX && Math.abs(deltaY) < DRAG_MIN_PX) {
                    onScopeAppointment?.(d.appt);
                    return null;
                }

                // Compute new geometry. Time delta = horizontal pixels →
                // minutes. Door delta = which row index the cursor is over.
                const newLeft = d.origLeft + deltaX;
                const snappedLeft = snapPxToMinutes(newLeft, hourWidth);

                // Find the door the cursor's Y is over (variable row heights)
                const gridEl = document.querySelector('[data-grid-container]') as HTMLElement | null;
                let newDoorId: number | null = d.appt.dockDoorId ?? null;
                if (gridEl) {
                    const rect = gridEl.getBoundingClientRect();
                    const yInGrid = d.pointerY - rect.top - HEADER_HEIGHT_PX - capOffset + (scrollRef.current?.scrollTop ?? 0);
                    const targetDoorId = doorIdAtY(yInGrid);
                    if (targetDoorId !== null) {
                        // ⭐ THE QUEUE ROW IS A DESTINATION, NOT A DEAD ZONE. Dropping
                        // here un-assigns the door — the one gesture that takes a truck
                        // off a door without cancelling it. ⛔ The old code refused the
                        // drop and silently kept the ORIGINAL door, so the block sprang
                        // back and the scheduler had no idea why.
                        //
                        // ⚠️ THE SENTINEL IS TRANSLATED, NOT PASSED. UNASSIGNED_DOOR_ID
                        // is 0, a drawing-layer id for a synthetic row; the API's word
                        // for "no door" is null. Sending 0 would be a door id that does
                        // not exist.
                        newDoorId = targetDoorId === UNASSIGNED_DOOR_ID ? null : targetDoorId;
                    }
                }

                // Translate snapped pixel offset → ISO UTC datetimes
                const startMinutes = (snappedLeft / hourWidth) * 60 + startHour * 60;
                /**
                 * ⛔⛔ A PACKED CHIP'S DURATION IS A FICTION — DO NOT SEND IT.
                 *
                 * ⚠️ THE BUG PETE HIT, 2026-08-24: he dragged a 15-minute-looking chip
                 * to 08:30 and it came back at 07:30. A day-only appointment is STORED
                 * spanning the whole operating window (massCreate writes
                 * windowStartUtc→windowEndUtc, 07:30–17:00 = 570 minutes) and DRAWN as a
                 * 15-minute chip packed at the day start. The two numbers have nothing to
                 * do with each other.
                 *
                 * ⛔ SO READING THE DURATION OFF THE DTO SENT 08:30 → 18:00 — the truck
                 * would have claimed the rest of the day. It stored fine, which is why
                 * nothing complained.
                 *
                 * ⭐ PETE'S OWN ANSWER IS THE RIGHT ONE: *"Shouldn't it auto expand it to
                 * the default 1 hour starting time wherever you dragged it?"* Dragging an
                 * untimed truck onto the grid IS the act of giving it a time, so it takes
                 * the facility's slot length — the same default a new appointment gets.
                 *
                 * ⚠️ `spanOf.get(id)?.packed` IS THE TEST, NOT isDayOnly. A day-only truck
                 * that has ARRIVED is drawn at its real dwell (hasActualSpan) and is not
                 * packed — dragging THAT should keep the duration on screen, which is the
                 * one the DTO no longer describes either. The packed flag is the only
                 * thing that means "the width you see is invented".
                 */
                const packed = spanOf.get(d.appt.appointmentId)?.packed === true;
                const durationMinutes = packed
                    ? slotMinutes
                    : differenceInMinutes(
                        parseISO(d.appt.scheduledEndUtc),
                        parseISO(d.appt.scheduledStartUtc),
                    );
                const startLocal = minutesToLocalDateTime(startMinutes, date);
                const endLocal = minutesToLocalDateTime(startMinutes + durationMinutes, date);

                onMoveAppointment({
                    appointmentId: d.appt.appointmentId,
                    dockDoorId: newDoorId,
                    newStartUtc: new Date(startLocal).toISOString(),
                    newEndUtc: new Date(endLocal).toISOString(),
                });
                return null;
            });
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        // ⚠️ spanOf ADDED 2026-08-24 — the commit path now asks whether the chip it is
        // moving is a PACKED one, and a stale spanOf would answer about the previous
        // day's layout. Two sibling handlers have the same omission (see #152).
    /* ⚠️ `onScopeAppointment` JOINS THE DEPS because the short-drag branch now calls
       it; a stale closure here would scope whatever load was under the cursor when
       this callback was last built. */
    }, [activeDrag, date, doors, doorIdAtY, onCreateInSlot, onMoveAppointment, onScopeAppointment, slotMinutes, hourWidth, totalWidth, startHour, endHour, spanOf]);

    // Mouse down on empty row area → start create drag
    const handleRowMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>, dockDoorId: number | null) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-appointment-block]')) return;
            if (e.button !== 0) return;
            /**
             * ⛔⛔ THE SECOND HALF OF THE 2026-09-10 FIX, AND THE ONE THAT WAS
             * ACTUALLY BITING PETE.
             *
             * ⚠️ THE FIRST PASS ONLY FIXED `handleApptMouseDown` — the CHIP. This
             * is the ROW UNDER the chips, where a drag CREATES an appointment,
             * and it never suppressed the browser's default either. Dragging
             * anywhere on empty grid still painted the whole page blue, so the
             * bug looked unfixed.
             *
             * ⭐ THE LESSON IS §4.14's: THE FIX HAD TO COVER THE POPULATION, NOT
             * THE INSTANCE. There are five mouse-drag starts in this file; the
             * deploy guard now checks every one of them, not the one that was
             * reported.
             */
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const startX = Math.max(0, Math.min(totalWidth, e.clientX - rect.left));
            setActiveDrag({ kind: 'create', dockDoorId, startX, currentX: startX });
        },
        [totalWidth],
    );

    // Mouse down on an appointment block → start move drag (or potential click)
    const handleApptMouseDown = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, appt: AppointmentDto, rowIdx: number) => {
            if (e.button !== 0) return;       // only primary button

            /**
             * ---- ⛔⛔ preventDefault() COMES FIRST, BEFORE THE TERMINAL RETURN --
             *
             * > Pete, 2026-09-10: *"When I click and try to drag on this
             * > appointment, everything gets highlighted on the screen"*
             *
             * ⚠️ THIS GRID DRAGS WITH MOUSE EVENTS, NOT HTML5 DRAG-AND-DROP. The
             * browser's default action for a mousedown-and-move is therefore
             * **start a text selection** — and the only thing suppressing it is
             * this call.
             *
             * ⛔ IT USED TO SIT BELOW THE `isTerminal` EARLY RETURN, so dragging a
             * DEPARTED / CANCELLED / NOSHOW / ARRIVED appointment returned before
             * ever reaching it and painted the whole page blue. ⚠️ The bug was
             * invisible on the eight appointments that CAN be dragged and obvious
             * on the one that cannot — which is why it read as "drag is broken"
             * rather than "one branch misses a call".
             *
             * ⭐ SUPPRESSING SELECTION IS CORRECT ON EVERY PATH, draggable or not.
             * Nobody selects text by dragging across a dock schedule.
             */
            e.preventDefault();

            // Terminal-state appointments can't be rescheduled. We still
            // allow click-to-select (mouseup with no drag will fire that).
            const isTerminal =
                appt.statusCode === 'DEPARTED' ||
                appt.statusCode === 'CANCELLED' ||
                appt.statusCode === 'NOSHOW' ||
                appt.statusCode === 'ARRIVED';   // arrived = truck physically here
            if (isTerminal) {
                /* ⛔ SCOPES, like the short drag above — the two paths must agree or a
                   DEPARTED load would behave differently from an APPROVED one on the
                   same gesture, which reads as the board being broken for some trucks.
                   ⚠️ THIS FIRES ON **mousedown**, not mouseup (a terminal chip starts no
                   drag, so there is nothing to measure). A double-click therefore scopes
                   twice before opening — idempotent, same appointment, same scope. */
                onScopeAppointment?.(appt);
                return;
            }
            const block = computeBlockGeometry(appt, date, hourWidth, startHour, endHour, spanOf);
            if (!block) return;
            // Vertical origin = the door row's cumulative top + this appt's lane
            // offset within the door (so the ghost starts exactly on the block).
            const doorKey = appt.dockDoorId ?? UNASSIGNED_DOOR_ID;
            const doorTop = layout.topByDoor.get(doorKey) ?? (rowIdx * LANE_HEIGHT_PX);
            const lane = layout.lanesByDoor.get(doorKey)?.get(appt.appointmentId) ?? 0;
            setActiveDrag({
                kind: 'move',
                appt,
                origLeft: block.left,
                origWidth: block.width,
                origRowTop: HEADER_HEIGHT_PX + capOffset + doorTop + lane * LANE_HEIGHT_PX,
                pointerStartX: e.clientX,
                pointerStartY: e.clientY,
                pointerX: e.clientX,
                pointerY: e.clientY,
            });
        },
        /* ⚠️ `onScopeAppointment`, not `onSelectAppointment` — the terminal-state branch
           above stopped calling the latter. */
        [date, layout, onScopeAppointment, hourWidth, capOffset, startHour, endHour],
    );

    // Mouse down on a resize handle → start a resize drag from that edge.
    const handleApptResizeMouseDown = useCallback(
        (e: React.MouseEvent<HTMLSpanElement>, appt: AppointmentDto, edge: 'start' | 'end') => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();            // don't let the block's move-drag also start
            const block = computeBlockGeometry(appt, date, hourWidth, startHour, endHour, spanOf);
            if (!block) return;
            setActiveDrag({
                kind: 'resize',
                appt,
                edge,
                origLeft: block.left,
                origWidth: block.width,
                pointerStartX: e.clientX,
                pointerX: e.clientX,
            });
        },
        [date, hourWidth, startHour, endHour],
    );

    // Right-click → page-level context menu
    const handleApptContextMenu = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, appt: AppointmentDto) => {
            if (!onRequestContextMenu) return;
            e.preventDefault();
            onRequestContextMenu({ appt, x: e.clientX, y: e.clientY });
        },
        [onRequestContextMenu],
    );

    return (
        <div className={`flex flex-col h-full overflow-hidden border ${FRAME_BORDER} rounded-md bg-card`}>
            {/* ---- Header date strip ---- */}
            <div className={BOARD_HEADER_STRIP_CLASS}>
                {/**
                  * ---- ⭐ ONE ROW: THE FILTER, THEN THE TALLY, PUSHED APART ----
                  *
                  * > Pete, 2026-09-19: *"The status and Appointments Doors should be on
                  * > the same line"*
                  *
                  * ⛔ THIS IS WHERE THEY STARTED, AND IT IS NOT A REVERSION. What broke
                  * the original one-row layout was the DATE HEADING sharing the row: the
                  * filter got whatever the date and the tally left over, and wrapped.
                  * ⭐ The heading is gone now, so one row fits — and that is why moving
                  * the tally up and then back down is not a circle. The thing that had
                  * to leave was neither of them.
                  *
                  * ---- ⚠️ THE TWO CLASSES THAT KEEP IT ON ONE LINE ----
                  *
                  * ** `min-w-0` on the filter. ** A flex child's default `min-width:auto`
                  * refuses to shrink below its content, so without this the filter pushes
                  * the tally off the edge instead of wrapping inside itself.
                  * ** `shrink-0` on the tally. ** It is short, it is the only count on
                  * screen, and squeezing it is never the right trade — on a narrow
                  * window the FILTER should give, because it can wrap and still read.
                  *
                  * ⛔ NOT `flex-1` and NOT `justify-center` on the filter. Both were on
                  * the original and both were wrong once the row had three occupants:
                  * they centred the filter in the leftovers rather than letting it start
                  * where the eye does.
                  */}
                {/* ⭐ F466 (Pete, 2026-10-08): *"can we move the All Status line down to the next line - same as
                    Limits OUT line? When the users have a small screen, this wraps on multiple lines"* - Day only
                    (*"week and month, it can stay where it is"*). Row 1 = the open-item chips + the tally (right);
                    row 2 = the status / work / WMS filters (left) + Limits (right). */}
                <div className="flex items-center justify-end gap-3">
                    {/**
                      * ---- ⛔ THE DATE HEADING USED TO BE HERE, AND IS SUPPRESSED ----
                      *
                      * > Pete, 2026-09-19: *"The date is actually redundant above the
                      * > filter. Lets make it invisible for now. plus that will match the
                      * > week and month views"*
                      *
                      * ⭐ The date picker sits directly above this strip and already says
                      * which day is on screen; Week and Month draw no such heading.
                      * ⛔ §0.3 — RECORDED, NOT DELETED, so "for now" can be undone without
                      * archaeology. It opened this row, verbatim:
                      *
                      *     ~~<div className="font-medium">~~
                      *     ~~    {format(parseISO(date), 'EEEE, MMMM d, yyyy')}~~
                      *     ~~</div>~~
                      *
                      * ✅ MEASURED BEFORE REMOVING IT: `format` and `parseISO` are used
                      * elsewhere in this file (the hour ruler, the appointment tooltip),
                      * so the `date-fns` import does NOT become unused. ⛔⛤ `noUnusedLocals`
                      * is on, and that is exactly how deleting `STATUS_LABEL` broke the
                      * build earlier the same day (§1AV.166).
                      *
                      * ⚠️⚠️ AND IT MAKES THE DATE PICKER LOAD-BEARING. With this gone, the
                      * toolbar input is the ONLY thing on the Day view naming the day —
                      * which is why it is `font-semibold` in Calendar.tsx. Restoring this
                      * heading is what would make that bold redundant, not the reverse.
                      */}
                    {/* ⚠️ whitespace-nowrap + shrink-0: the only count on screen never
                        breaks across lines and never gets squeezed. */}
                    {/* ⛔ ~~{doors.length} door{...}~~ — REMOVED 2026-09-19. Pete: *"I
                        don't need the Doors Count."* ⭐ It never varied within a view: the
                        facility has the doors it has, and a number that is the same on
                        every screen of every day is furniture, not information. */}
                    {/* ⭐ THE TALLY AND ITS UNDO, ADJACENT (Pete, 2026-09-27) — the same
                        pairing `Calendar.tsx` draws for Week and Month, with the SAME
                        element rather than a second copy of it.
                        ⚠️ NOTE WHICH NUMBER THIS IS: `appointments.length` is the FILTERED
                        list, while the Week/Month tally shows the UNFILTERED `totalCount`.
                        The two headers have always disagreed about what "N appointments"
                        counts; putting Ⓡ beside it makes Day's reading the coherent one —
                        "1 appointment" plus a pulsing reset says you are narrowed — and
                        makes Week/Month's the odd one. ⬜ Open item, not silently changed
                        here: a tally that changes meaning is a number somebody is reading. */}
                    <div className="flex shrink-0 items-center gap-2">
                        {headerAlert}
                        <div className="whitespace-nowrap text-xs text-muted-foreground">
                            {appointments.length} appointment{appointments.length === 1 ? '' : 's'}
                        </div>
                        {resetButton}
                    </div>
                </div>
                <div className="mt-1 flex items-center justify-between gap-3">
                    {/**
                      * ---- ⭐⭐ THE TWO FILTERS ARE ONE OCCUPANT (Pete, 2026-09-25) -------
                      *
                      * > *"These need to move to just past Dedicated. You can separate
                      * > with a |"*
                      *
                      * ⛔ THEY ALREADY WERE JUST PAST DEDICATED IN THE MARKUP, AND THE RULE
                      * WAS ALREADY THERE. The gap was `justify-between` on the parent. The
                      * note above says the row has "three occupants" - status filter, work
                      * filter, tally - and justify-between with THREE children puts one in
                      * the MIDDLE. That middle child was the work filter, stranded halfway
                      * across the header with the rule invisible beside it.
                      *   ** justify-between DISTRIBUTES CHILDREN, IT DOES NOT ALIGN THEM.
                      *      ADDING A THIRD CHILD SILENTLY REDEFINES WHAT THE FIRST TWO
                      *      MEANT. **
                      *
                      * ⭐ SO THE ROW IS TWO OCCUPANTS AGAIN: the filter GROUP, and the
                      * tally. Everything the note above protects is untouched - no flex-1
                      * and no justify-center on the filter, the tally still shrink-0, the
                      * filter still starts where the eye does.
                      *
                      * ⚠️ `min-w-0` ON THE GROUP, not just on the bars inside it. A flex
                      * child refuses to shrink below its content, so without it this group
                      * would push the tally off the edge - the exact fault the note above
                      * describes, one level further out.
                      */}
                    <div className="flex min-w-0 items-center gap-3">
                    {/**
                      * ---- ✅✅ DECISION 184 — THE FILTER LIVES HERE NOW (2026-09-18) ----
                      *
                      * > Pete: *"The top filter needs to be moved to replace the middle
                      * > status between date and appointments - doors. Its rundandant …
                      * > Moves only on the day screen, not the week or month."*
                      *
                      * ⛔⛤ ~~COUNTS CARRY THE STATUS COLOUR … BLOCKs are door outages and
                      * are excluded -- they are not trucks.~~ **REPLACED, AND THE ROW IT
                      * REPLACES WAS WRONG IN THREE WAYS:**
                      *
                      *   1. ** IT HARDCODED ITS OWN SIX-ELEMENT ARRAY ** — the second list
                      *      the big bar's own comment exists to prevent (#254).
                      *   2. ** IT OMITTED `BLOCK`. ** Six of seven.
                      *   3. ** ITS COUNTS WERE COUNTS OF THE FILTERED SET. ** This grid is
                      *      handed `filterAppts(...)`, so filtering to "Approved" drove
                      *      every other count in this header to 0.
                      *
                      *      ** A COUNT COMPUTED FROM WHAT SURVIVED THE FILTER CANNOT TELL
                      *         YOU WHAT THE FILTER IS HIDING, WHICH IS THE ONLY QUESTION
                      *         IT WAS ON SCREEN TO ANSWER. **
                      *
                      * ⭐ `statusCounts` / `totalCount` come from the page's UNFILTERED
                      * source, so these numbers describe the day, not the selection.
                      * ⚠️ `showOverlayLegend={false}` — the capacity-band row directly
                      * below already draws a `Limits` legend. Twice in one header is the
                      * duplication this change exists to remove.
                      * ⛔ Week and Month keep the big bar in `Calendar.tsx` (decision 184
                      * reverses decision 110 on that point).
                      */}
                    {/**
                      * ⭐ Pete, 2026-09-19: *"Put the Limits purple icon back on the top
                      * bar"*. ⛔ `showOverlayLegend={false}` is GONE.
                      * ⚠️ ~~the capacity-band row directly below already draws a `Limits`
                      * legend~~ — it draws the word `Limits` followed by the ACTUAL BANDS
                      * ("7:30am-5:00pm: max 4"), and only when there are any. That is a
                      * readout, not a key. ** A VALUE AND A COLOUR KEY ARE NOT THE SAME
                      * THING, and I struck the swatch on the strength of the word. **
                      */}
                    <StatusFilterBar
                        dense
                        /* ⛔ ~~hideStatuses={['BLOCK']}~~ -- STRUCK 2026-09-19, hours after
                           it landed. Pete: *"you can make blocked invisible for now"* ->
                           *"Looks like Blocked can fit on Day screen now."*
                           ⭐ "For now" meant "until the row has room", and the room came from
                           removing the door count and the date heading, not from hiding a
                           status. ** THE CROWDING WAS THE PROBLEM; THE STATUS WAS NEVER THE
                           PROBLEM, AND HIDING IT WAS A MEASURE OF THE CROWDING. **
                           ✅ All seven statuses now render in all three views, so the prop
                           has no caller. ⚠️ It stays on the component: it is the only correct
                           way to hide one, and its `!checked` guard is the part worth keeping
                           (see StatusFilterBar). */
                        statusFilter={statusFilter}
                        toggleStatus={toggleStatus}
                        setStatusFilter={setStatusFilter}
                        statusCounts={statusCounts}
                        totalCount={totalCount}
                        className="min-w-0"
                    />
                    {/**
                      * ⭐⭐ THE WORK AXIS, BESIDE THE STATUS ROW — identical markup to the
                      * one `Calendar.tsx` renders for Week and Month, because
                      * ⛔ DEDUPLICATING A COMPONENT DOES NOT DEDUPLICATE ITS SURROUNDINGS,
                      * and the surroundings are most of what "looks the same" means
                      * (§1AV, on this very strip).
                      * ⚠️ THE RULE BETWEEN THE TWO ROWS IS LOAD-BEARING: without it they read
                      * as one row that wrapped, and `All Status` looks like it should clear
                      * both. It clears only its own — the two filters AND.
                      */}
                    <span aria-hidden="true" className="h-3 w-px shrink-0 bg-border" />
                    <WorkFilterBar
                        dense
                        workFilter={workFilter}
                        toggleWork={toggleWork}
                        workCounts={workCounts}
                        className="min-w-0"
                    />
                    {/**
                      * ⭐⭐ THE WMS AXIS, THIRD — identical markup to the one
                      * `Calendar.tsx` renders for Week and Month, for the reason stated
                      * directly above about the work row: ⛔ DEDUPLICATING A COMPONENT
                      * DOES NOT DEDUPLICATE ITS SURROUNDINGS.
                      * ⚠️ ITS OWN RULE, not a shared one with the work row. Three chip
                      * rows with two rules read as three filters; with one rule they read
                      * as two, and a scheduler would expect the wrong thing to clear.
                      */}
                    <span aria-hidden="true" className="h-3 w-px shrink-0 bg-border" />
                    <WmsFilterBar
                        dense
                        wmsFilter={wmsFilter}
                        toggleWms={toggleWms}
                        wmsCounts={wmsCounts}
                        className="min-w-0"
                    />
                    {/* ⭐ F228 — Pete: "What does the red icon mean again? There is no where to find out?" */}
                    <CalendarIconKey />
                    </div>
                    {capacityBands.length > 0 && (
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                            <span className="font-medium text-foreground/70">Limits</span>
                            {capacityBands.map((b, i) => {
                                const badge = (k: 'IN' | 'OUT') => (
                                    <span className={cn(
                                        'inline-block px-1 rounded-sm text-[9px] font-semibold leading-tight border',
                                        k === 'IN' ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-orange-100 text-orange-800 border-orange-300',
                                    )}>{k}</span>
                                );
                                return (
                                    <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">
                                        {b.direction === 'INBOUND' ? badge('IN')
                                            : b.direction === 'OUTBOUND' ? badge('OUT')
                                            : (<span className="inline-flex gap-0.5">{badge('IN')}{badge('OUT')}</span>)}
                                        {/* ⛔⛤ 2026-09-22 -- ~~{fmtCapTime(start)}-{fmtCapTime(end)}: max N~~
                                            ⭐ Pete: *"The time in this area should be removed"* -- for an
                                            AT-ONCE band, where the window is one span and the bar below
                                            already draws it.
                                            ⛔⛔ BUT A PER-HOUR BAND KEEPS ITS RANGE, because "3/hr" is
                                            meaningless without saying over what. It is also ONE entry
                                            here rather than nine: Pete, *"we can't list out time because
                                            there will be too many of them."* The nine bars are on the
                                            grid; this line is the rule, not the hours.
                                              ** A SUMMARY THAT REPEATS EVERY ROW IS NOT A SUMMARY. ** */}
                                        <span>
                                            {b.perHour
                                                ? `${b.maxConcurrent}/hr ${fmtCapTime(b.startMinutes)}–${fmtCapTime(b.endMinutes)}`
                                                : `max ${b.maxConcurrent}`}
                                        </span>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ---- Scrollable grid ---- */}
            <div ref={scrollRef} className="flex-1 overflow-auto relative">
                <div
                    className="relative"
                    data-grid-container
                    style={{
                        width: DOOR_LABEL_WIDTH_PX + totalWidth,
                        minHeight: HEADER_HEIGHT_PX + capOffset + layout.totalHeight,
                    }}
                >
                    {/* ---- Time header (sticky top) ---- */}
                    <div
                        className="sticky top-0 z-20 flex bg-card border-b shadow-sm"
                        style={{ height: HEADER_HEIGHT_PX }}
                    >
                        <div
                            className="sticky left-0 z-30 bg-card border-r flex items-center gap-1 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                            style={{ width: DOOR_LABEL_WIDTH_PX }}
                        >
                            {/**
                              * ---- ⛔ ~~`Dedicated` LIVES WITH THE DOORS~~ — NEVER SHIPPED.
                              * ---- ⭐ THE COUNT LIVES WITH THE DOORS INSTEAD (Pete, 2026-09-19)
                              *
                              * > *"put the dedicated yellow bar next to DOORS on the first
                              * > line of the calendar"* → ⭐ then, before it shipped:
                              * > *"Actually, not sure we need dedicated because it shows up
                              * > on the yellow bar on each row in a dedicated door correct?"*
                              *
                              * ✅ **MEASURED, AND HE IS RIGHT.** A dedication draws itself on
                              * its own door's row, THREE times over, and two of the three are
                              * in words:
                              *   1. the window band — `bg-amber-200/35 border-x border-amber-400/40`
                              *   2. the drag handle on top of it — `bg-amber-300/70`, and it
                              *      **carries `{b.label}` as text**, i.e. the carrier's name.
                              *   3. the door-label cell — `bg-amber-100 text-amber-800` badge
                              *      reading **`{door.dedicatedTo}-Dedicated`**, with its own
                              *      `bg-amber-500` dot.
                              *
                              * ⛔⛔ **A LEGEND FOR SOMETHING THE GRID ALREADY SPELLS OUT IN
                              * WORDS IS WALLPAPER (§269).** A key earns its pixels by naming a
                              * colour whose meaning is NOT written where the colour appears.
                              * `Limits` earns it — those violet hatches carry no text. This did
                              * not: the amber band is captioned with the carrier, and the door
                              * cell says the word "Dedicated" outright.
                              *
                              * ⭐ **SO THE SLOT WENT TO THE NUMBER INSTEAD.** Pete: *"If that's
                              * the case the put the doors count in parenthesis next to
                              * DOORS (23) on the first line"*. ⚠️ The count was removed from the
                              * top tally (see above) because it was furniture THERE — the same
                              * number on every screen of every day, sat beside a figure that
                              * changes hourly. Beside the column it counts, the same number is
                              * a header, not a statistic. ⛔ **THE COUNT DID NOT BECOME USEFUL;
                              * IT MOVED SOMEWHERE THE READER IS ALREADY ASKING THE QUESTION.**
                              *
                              * ⛔ `doors.length`, **NOT `rows.length`** — `rows` prepends the
                              * synthetic QUEUE/Unassigned row, which is not a door. It is the
                              * same expression the struck tally used, so the number Pete has
                              * been reading does not change, only where it sits.
                              *
                              * ⚠️ The cell is `uppercase`, so `Doors` renders as **DOORS** —
                              * exactly what Pete wrote. No case override is needed here, and
                              * none should be added: the old `Dedicated` chip needed three
                              * (`normal-case font-normal tracking-normal`) precisely because it
                              * was a foreign object in a heading cell. A count is not.
                              */}
                            <span className="whitespace-nowrap">
                                Doors ({doors.length})
                            </span>
                            {/**
                              * ---- ⭐⭐ BUSY DOORS TO THE TOP, ON A CLICK (2026-09-10) ----
                              *
                              * > Pete: *"a icon up next to Door that rearrancges the doors
                              * > by if they have meetings or not, then by dock door order"*
                              * > … *"Click on it again and it resets back to the original
                              * > layout."*
                              *
                              * ⛔ IT IS A TOGGLE, NOT A SORT PLUS A RESET BUTTON. Pete
                              * described the second click as the reset, so there is one
                              * control with two states rather than two controls that can
                              * disagree about which is in force.
                              *
                              * ⛔⛔ AND IT NEVER RUNS ITSELF — see `lib/doorOrder.ts`. A row
                              * that re-sorts while somebody is dragging an appointment onto
                              * it is a real hazard, not a cosmetic one.
                              */}
                            <button
                                type="button"
                                onClick={() => setBusyFirst(v => !v)}
                                aria-pressed={busyFirst}
                                /**
                                 * ---- ⛔⛔ ~~"put them back in door order"~~ — IT LIED ------
                                 *
                                 * > Pete, 2026-09-10: *"The Sort doesn't put them back to the
                                 * > orignal order now. Does it remember the previous order or
                                 * > does it look at the Dock Door Setup config?"*
                                 *
                                 * ⚠️ IT REMEMBERS — deliberately, see `lib/doorOrder.ts`. The
                                 * toggle's OFF state is the order the board had before the
                                 * sort, which is HER dragged order once she has made one.
                                 * ⛔ BUT THE TOOLTIP DESCRIBED THE OTHER CASE — the one where
                                 * she has never dragged a row — so the only sentence on screen
                                 * explaining the behaviour was wrong precisely for the person
                                 * who would go looking for it.
                                 *
                                 * ⭐ SO IT NAMES WHICH ORDER IT MEANS. Two states, two
                                 * sentences; a control that says "back" without saying back to
                                 * WHAT is how this got reported as broken when it was working.
                                 */
                                /* ⚠️ `!doorsAreInSiteOrder`, NOT `manualOrder` — the same
                                   correction as the reset button below. Offering to
                                   "go back to the order you dragged" when that order IS
                                   the site's order names a distinction the scheduler
                                   cannot see on screen. */
                                title={busyFirst
                                    ? (!doorsAreInSiteOrder
                                        ? 'Doors with appointments are on top — click to go back to the order you dragged'
                                        : 'Doors with appointments are on top — click to go back to the site’s door order')
                                    : 'Put doors with appointments on top; empty doors to the bottom'}
                                className={cn(
                                    'shrink-0 rounded p-0.5 transition-colors hover:bg-muted',
                                    busyFirst ? 'bg-primary/15 text-primary' : 'text-muted-foreground',
                                )}
                            >
                                <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                                <span className="sr-only">
                                    {busyFirst
                                        ? (!doorsAreInSiteOrder ? 'Go back to the order you dragged' : 'Go back to the site’s door order')
                                        : 'Sort doors with appointments to the top'}
                                </span>
                            </button>
                            {/**
                              * ---- ⭐⭐ #76 / DECISION 213 — ASCENDING / DESCENDING BY NAME ----
                              *
                              * > Pete, 2026-09-22: *"We need to add Ascending/Descending to the
                              * > Door Column (maybe arrows)?"*
                              *
                              * ⛔⛔ A SECOND CONTROL, NOT A CYCLE THROUGH THE ONE ABOVE. Pete
                              * chose this: the toggle above GROUPS (busy doors on top), these
                              * arrows ORDER. Four states behind one icon would leave the
                              * scheduler unable to tell which one is in force without reading a
                              * tooltip.
                              *   ** A CONTROL WITH FOUR STATES IS A CONTROL EVERY READER
                              *      DISAGREES ABOUT. **
                              *
                              * ⭐ THREE STATES, CYCLING off → asc → desc → off. The off state is
                              * "the order on the board" — the site's order, or the one she
                              * dragged. ⛔ A plain two-way toggle would have no way to say
                              * "leave my arrangement alone", and the arrangement is the thing
                              * she spent time on.
                              *
                              * ⚠️ THE ICON REPORTS THE CURRENT STATE, NOT THE NEXT ONE. A↓Z when
                              * ascending, Z↑A when descending, and the neutral A↓Z greyed when
                              * off. A control that shows what it will DO next reads as a claim
                              * about what is on screen now, and that is how "the sort doesn't
                              * work" gets reported about a sort that is working.
                              */}
                            <button
                                type="button"
                                onClick={() => setDoorSort(v => (v === null ? 'asc' : v === 'asc' ? 'desc' : null))}
                                aria-pressed={doorSort !== null}
                                title={doorSort === 'asc'
                                    ? 'Doors are in name order, A to Z — click for Z to A'
                                    : doorSort === 'desc'
                                        ? (!doorsAreInSiteOrder
                                            ? 'Doors are in name order, Z to A — click to go back to the order you dragged'
                                            : 'Doors are in name order, Z to A — click to go back to the site’s door order')
                                        : 'Sort doors by name (9 before 10, not after 89)'}
                                className={cn(
                                    'shrink-0 rounded p-0.5 transition-colors hover:bg-muted',
                                    doorSort ? 'bg-primary/15 text-primary' : 'text-muted-foreground',
                                )}
                            >
                                {doorSort === 'desc'
                                    ? <ArrowUpAZ className="h-3.5 w-3.5" />
                                    : <ArrowDownAZ className="h-3.5 w-3.5" />}
                                <span className="sr-only">
                                    {doorSort === 'asc' ? 'Doors sorted by name, A to Z'
                                        : doorSort === 'desc' ? 'Doors sorted by name, Z to A'
                                            : 'Sort doors by name'}
                                </span>
                            </button>
                            {/* ⭐ ONLY WHEN THERE IS SOMETHING TO UNDO. Pete's reset is the
                                toggle above; this is the separate one that clears a
                                DRAGGED arrangement, and offering it when she has never
                                dragged a row is a control that does nothing. */}
                            {!doorsAreInSiteOrder && (
                                <button
                                    type="button"
                                    onClick={() => { clearDoorOrder(facilityCode); setManualOrder(null); }}
                                    title="Forget the order you dragged and go back to the site's door order"
                                    className="flex shrink-0 items-center gap-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    {/**
                                      * ⭐ THE WORDS, NOT JUST THE GLYPH (2026-09-10). This
                                      * button was a bare ↺ whose only explanation was its
                                      * tooltip, and Pete could not tell what it did — the
                                      * same report that exposed the lying sort tooltip above.
                                      * ⚠️ AN ICON THAT UNDOES A STATE YOU CANNOT SEE has to
                                      * say so on its face; hovering to find out only works
                                      * for somebody who already suspects it exists.
                                      */}
                                    <span className="text-[9px] font-medium leading-none">Site order</span>
                                </button>
                            )}
                        </div>
                        <div
                            className="relative cursor-col-resize"
                            style={{ width: totalWidth }}
                            onMouseMove={(e) => {
                                if (activeDrag) { setHoverGuideLeft(null); return; }
                                const rect = e.currentTarget.getBoundingClientRect();
                                const xTime = e.clientX - rect.left;
                                if (xTime < 0 || xTime > totalWidth) { setHoverGuideLeft(null); return; }
                                const snappedMin = Math.round((xTime / hourWidth) * 60 / 15) * 15;
                                setHoverGuideLeft(DOOR_LABEL_WIDTH_PX + (snappedMin / 60) * hourWidth);
                            }}
                            onMouseLeave={() => setHoverGuideLeft(null)}
                        >
                            {Array.from({ length: totalHours + 1 }, (_, i) => {
                                const hour = startHour + i;
                                const left = i * hourWidth;
                                return (
                                    <div
                                        key={hour}
                                        className="absolute top-0 bottom-0 flex flex-col justify-start pt-1 text-tabular text-xs text-muted-foreground"
                                        style={{ left, transform: 'translateX(-50%)' }}
                                    >
                                        <span>{format(new Date(2000, 0, 1, hour), 'h a')}</span>
                                    </div>
                                );
                            })}
                            {/* Quarter-hour tick marks along the bottom of the header:
                                hour (:00) is tallest + boldest, :30 medium, :15 / :45 shortest. */}
                            {Array.from({ length: totalHours + 1 }, (_, i) => (
                                <div
                                    key={`htick-${i}`}
                                    className="absolute bottom-0 w-0.5 bg-muted-foreground"
                                    style={{ left: i * hourWidth, height: 10, transform: 'translateX(-50%)' }}
                                />
                            ))}
                            {Array.from({ length: totalHours }, (_, i) =>
                                [0.25, 0.5, 0.75].map((frac) => {
                                    const isHalf = frac === 0.5;
                                    return (
                                        <div
                                            key={`tick-${i}-${frac}`}
                                            className="absolute bottom-0 w-px bg-muted-foreground/60"
                                            style={{ left: i * hourWidth + hourWidth * frac, height: isHalf ? 7 : 4 }}
                                        />
                                    );
                                }),
                            )}
                        </div>
                    </div>

                    {/* ---- Appointment-limit marker (IN/OUT badges + tooltips; band renders per-row below) ---- */}
                    {capWindows.length > 0 && (
                        <div
                            className="sticky z-[15] flex"
                            style={{ top: HEADER_HEIGHT_PX, height: 18 }}
                        >
                            <div className="sticky left-0 z-[16] bg-card" style={{ width: DOOR_LABEL_WIDTH_PX }} />
                            <div className="relative" style={{ width: totalWidth }}>
                                {capWindows.map((w, i) => {
                                    const isDragging = !!capDrag && capDrag.origStart === w.startMinutes && capDrag.origEnd === w.endMinutes;
                                    const gStart = isDragging && capPreview ? capPreview.start : w.startMinutes;
                                    const gEnd   = isDragging && capPreview ? capPreview.end   : w.endMinutes;
                                    const g = computeBandGeometry(gStart, gEnd, hourWidth, startHour, endHour);
                                    if (!g) return null;
                                    const anyBlocked = w.blockedIn || w.blockedOut;
                                    const dragBands = w.bands.map(b => ({ capacityId: b.capacityId, maxConcurrent: b.maxConcurrent, effectiveStartDate: b.effectiveStartDate, effectiveEndDate: b.effectiveEndDate }));
                                    const startCapDrag = (mode: 'move' | 'resize-l' | 'resize-r', e: React.MouseEvent) => {
                                        if (!onEditLimitTimes || e.button !== 0) return;
                                        e.stopPropagation();
                                        capMovedRef.current = false;
                                        setCapPreview({ start: w.startMinutes, end: w.endMinutes });
                                        setCapDrag({ mode, bands: dragBands, origStart: w.startMinutes, origEnd: w.endMinutes, startClientX: e.clientX });
                                    };
                                    return (
                                        <div
                                            key={i}
                                            className={cn(
                                                'absolute top-0 bottom-0 flex items-center justify-center gap-1 px-1 overflow-hidden bg-violet-300 hover:brightness-95',
                                                capDrag ? 'cursor-grabbing' : 'cursor-grab',
                                                isDragging && 'ring-2 ring-violet-600 z-[17]',
                                            )}
                                            style={{
                                                left: g.left, width: g.width,
                                                ...(anyBlocked ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(109,40,217,0.40) 0, rgba(109,40,217,0.40) 3px, transparent 3px, transparent 10px)' } : {}),
                                            }}
                                            title="Drag to move · drag an edge to resize · click to edit · right-click for options"
                                            onMouseDown={(e) => startCapDrag('move', e)}
                                            onClick={() => { if (capMovedRef.current) { capMovedRef.current = false; return; } onOpenLimits?.(); }}
                                            onContextMenu={(e) => {
                                                if (!onRequestLimitMenu) return;
                                                e.preventDefault();
                                                onRequestLimitMenu({ bands: w.bands, x: e.clientX, y: e.clientY });
                                            }}
                                        >
                                            {onEditLimitTimes && (
                                                <>
                                                    <div className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-violet-600/40" onMouseDown={(e) => startCapDrag('resize-l', e)} onClick={(e) => e.stopPropagation()} />
                                                    <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-violet-600/40" onMouseDown={(e) => startCapDrag('resize-r', e)} onClick={(e) => e.stopPropagation()} />
                                                </>
                                            )}
                                            {/**
                                              * ⭐ THE UNIT, ONCE PER WINDOW (Pete,
                                              * 2026-09-23: *"shouldn't that state that
                                              * somehow in the purple bar"*).
                                              *
                                              * ⚠️ ONCE PER WINDOW, NOT ONCE PER BADGE.
                                              * `IN 3/3 /hr OUT 1/3 /hr` says the same
                                              * thing twice in a cell one hour wide; the
                                              * unit describes the WINDOW, which is what
                                              * a per-hour band expands into.
                                              * ⚠️ An at-once band shows nothing here --
                                              * it is the original meaning and the bar's
                                              * own span already states it.
                                              */}
                                            {w.badges.map((bd, j) => (
                                                <span
                                                    key={j}
                                                    title={bd.tip}
                                                    className={cn(
                                                        'inline-block px-1 rounded text-[9px] font-semibold tracking-wide leading-tight border cursor-help pointer-events-none',
                                                        bd.kind === 'IN'
                                                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                            : 'bg-orange-100 text-orange-800 border-orange-300',
                                                        bd.blocked && 'line-through',
                                                    )}
                                                >
                                                    {/* ⭐ `IN 2/3`, not `IN`. The cap alone says what the
                                                        rule is; the pair says whether there is room.
                                                        A blocked window keeps the bare letters -- a
                                                        count against a limit of 0 reads as an error. */}
                                                    {bd.label}
                                                </span>
                                            ))}
                                            {w.perHour && (
                                                <span className="text-[8px] font-semibold uppercase tracking-wide text-violet-900/70 pointer-events-none">
                                                    /hr
                                                </span>
                                            )}
                                            {isDragging && capPreview && (
                                                <span className="absolute -bottom-4 left-0 whitespace-nowrap rounded bg-violet-700 px-1 text-[9px] font-semibold text-white">
                                                    {fmtHM(capPreview.start)}–{fmtHM(capPreview.end)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ---- Door rows ---- */}
                    {rows.map((door, idx) => {
                        const rowTop = HEADER_HEIGHT_PX + capOffset + (layout.topByDoor.get(door.dockDoorId) ?? 0);
                        const rowHeight = layout.heightByDoor.get(door.dockDoorId) ?? ROW_HEIGHT_PX;
                        const rowAppts = apptsByDoor.get(door.dockDoorId) ?? [];
                        const laneOf = layout.lanesByDoor.get(door.dockDoorId);

                        return (
                            <div
                                key={door.dockDoorId}
                                className={cn(
                                    'absolute left-0 right-0 flex border-b bg-card',
                                )}
                                style={{ top: rowTop, height: rowHeight }}
                            >
                                {/* Door label (sticky left) */}
                                {(() => {
                                    const canEdit = !!onEditDoor && door.dockDoorId !== UNASSIGNED_DOOR_ID;
                                    return (
                                <div
                                    /**
                                      * ---- ⭐⭐ DRAG A ROW WHERE SHE WANTS IT (2026-09-10) ----
                                      *
                                      * > Pete: *"the scheduler should have the ability to drag
                                      * > a row above others to place it where she wants."*
                                      *
                                      * ⛔⛔ THE HANDLE IS THE LABEL, NOT THE ROW. The row body
                                      * carries create-drag, move-drag and resize for
                                      * appointments; making it also mean "reorder" would be
                                      * four meanings on one gesture. ⚠️ This 200px sticky cell
                                      * is the only part of a row with nothing else on it.
                                      *
                                      * ⛔ THE QUEUE ROW IS NOT DRAGGABLE — Pete: *"(not
                                      * unassigned)"*.
                                      */
                                    draggable={door.dockDoorId !== UNASSIGNED_DOOR_ID}
                                    onDragStart={(e) => {
                                        if (door.dockDoorId === UNASSIGNED_DOOR_ID) { e.preventDefault(); return; }
                                        rowDraggedRef.current = true;
                                        setRowDragId(door.dockDoorId);
                                        e.dataTransfer.setData(DOOR_ROW_DND_MIME, String(door.dockDoorId));
                                        e.dataTransfer.effectAllowed = 'move';
                                    }}
                                    onDragEnd={() => { setRowDragId(null); setRowOverId(null); }}
                                    onDragOver={(e) => {
                                        /* ⛔ ONLY A ROW DRAG. An appointment, an arrival or a
                                           scan passing over this cell must be left alone. */
                                        if (!isDoorRowDrag(e.dataTransfer)) return;
                                        if (door.dockDoorId === UNASSIGNED_DOOR_ID) return;
                                        /* ⛔⛔ SAY NO WITH THE CURSOR, NOT WITH A SILENT NO-OP.
                                           While the partition is on, a busy row cannot be placed
                                           among the idle ones -- the next render would hoist it
                                           straight back. ** A DROP TARGET THAT ACCEPTS A GESTURE
                                           IT WILL NOT HONOUR TEACHES THE WRONG RULE. **
                                           ⚠️ `rowDragId`, not the payload: `getData` is withheld
                                           during `dragover` by every browser. */
                                        if (!canDropDoorRow(doors, busyFirst, hasAppointments,
                                                            rowDragId, door.dockDoorId)) {
                                            e.dataTransfer.dropEffect = 'none';
                                            return;
                                        }
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                        setRowOverId(door.dockDoorId);
                                    }}
                                    onDragLeave={() => setRowOverId(
                                        (h) => (h === door.dockDoorId ? null : h))}
                                    onDrop={(e) => {
                                        if (!isDoorRowDrag(e.dataTransfer)) return;
                                        e.preventDefault();
                                        setRowOverId(null);
                                        const dragged = Number(e.dataTransfer.getData(DOOR_ROW_DND_MIME));
                                        if (!Number.isFinite(dragged)) return;
                                        /**
                                          * ⛔⛔⚤ ~~THE ORDER SHE IS LOOKING AT IS WHAT GETS
                                          * SAVED~~ — STRUCK 2026-09-19 (§0.3). It was half
                                          * right: saving the SERVER order would indeed
                                          * rearrange the board when she turned the sort off.
                                          * But saving the VISIBLE order froze the busy
                                          * partition into her saved arrangement, after which
                                          * the toggle could never change anything again.
                                          * ** A VIEW TOGGLE'S OUTPUT WAS PERSISTED AS A
                                          *    PREFERENCE, AND A PREFERENCE CANNOT BE TOGGLED
                                          *    OFF. **
                                          * ✅ `moveDoorInBaseOrder` applies the move to the
                                          * arrangement UNDERNEATH the partition. With the sort
                                          * off the two lists are identical, so this changes
                                          * nothing in the ordinary case.
                                          * ⛔ THE COMPOSITION LIVES IN `lib/doorOrder.ts` NOW.
                                          * Both boards used to build this id list out of their
                                          * own `rows`, which is two copies of the bug.
                                          */
                                        const next = moveDoorForDrop(
                                            doors, manualOrder, busyFirst, hasAppointments,
                                            dragged, door.dockDoorId);
                                        if (!next) return;
                                        setManualOrder(next);
                                        writeDoorOrder(facilityCode, next);
                                        /* ⛔⛔ ~~AND THE SORT GOES OFF~~ -- STRUCK 2026-09-19.
                                           It existed so the partition could not move the row she
                                           had just hand-placed. It no longer can: the placement
                                           is now expressed in the partition's own terms
                                           (`moveDoorForDrop`). ** TURNING IT OFF WAS THE UNWIND
                                           PETE REPORTED -- six of seven rows moving because one
                                           drop changed the order AND the view at once. ** */
                                    }}
                                    className={cn(
                                        'sticky left-0 z-[15] flex items-center gap-2 px-3 border-r bg-card',
                                        canEdit && 'cursor-pointer hover:bg-accent/60 transition-colors',
                                        door.dockDoorId !== UNASSIGNED_DOOR_ID && 'cursor-grab active:cursor-grabbing',
                                        rowDragId === door.dockDoorId && 'opacity-40',
                                        rowOverId === door.dockDoorId && 'ring-2 ring-inset ring-primary',
                                    )}
                                    style={{ width: DOOR_LABEL_WIDTH_PX, minWidth: DOOR_LABEL_WIDTH_PX }}
                                    title={
                                        canEdit
                                            ? `${door.name !== door.code ? `${door.name} (${door.code})` : door.code} — click to edit`
                                            : (door.name !== door.code ? `${door.name} (${door.code})` : door.code)
                                    }
                                    /* ⛔ A REORDER MUST NOT ALSO OPEN THE DOOR EDITOR. The
                                       browser fires `click` after a drag on some paths; the
                                       flag is set in `onDragStart` and consumed here. */
                                    onClick={canEdit ? () => {
                                        if (rowDraggedRef.current) { rowDraggedRef.current = false; return; }
                                        onEditDoor!(door.dockDoorId);
                                    } : undefined}
                                    role={canEdit ? 'button' : undefined}
                                >
                                    <div className="flex flex-col leading-tight min-w-0">
                                        <span className="flex items-center gap-1 min-w-0">
                                            <span className="text-sm font-medium truncate">
                                                {door.name || door.code}
                                            </span>
                                            {/**
                                              * ⭐ THE CAMERA PLACEHOLDER (Pete, 2026-08-25).
                                              * ⚠️ INSIDE THE CLICKABLE DOOR LABEL, and it is a
                                              * DISABLED button rather than a span precisely so it
                                              * swallows nothing: a disabled control does not fire
                                              * and does not bubble a click into "edit this door".
                                              * ⛔ Same component the labour card uses — one mark
                                              * for "there is a camera here", never two.
                                              */}
                                            {/**
                                              * ⭐ THE UNASSIGNED ROW GETS THE WALL VIEW.
                                              * Pete, 2026-08-25: *"In the Unassigned Queue -
                                              * the camera icon, lets make it look a tad
                                              * different - this will be a view of all the
                                              * cameras in the warehouse in a grid."*
                                              *
                                              * ⛔ AND IT IS A DIFFERENT GLYPH BECAUSE IT MAKES
                                              * A DIFFERENT PROMISE. One door's feed and every
                                              * camera in the building are not the same button,
                                              * and an identical icon would say they were.
                                              *
                                              * ⚠️ The sentinel row is not a door — it has no
                                              * camera of its own — so the wall view is the only
                                              * thing this position could honestly open.
                                              */}
                                            <DoorCameraPlaceholder
                                                doorName={door.name || door.code}
                                                variant={door.dockDoorId === UNASSIGNED_DOOR_ID
                                                    ? 'wall' : 'door'}
                                            />
                                        </span>
                                        {((door.name && door.name !== door.code) || door.dedicatedTo) && (
                                            <span className="flex items-center gap-1.5 mt-0.5 min-w-0">
                                                {door.name && door.name !== door.code && (
                                                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                                                        {door.code}
                                                    </span>
                                                )}
                                                {door.dedicatedTo && (() => {
                                                    // Real configured windows per dedication (all days). Timed
                                                    // rows show their times; null-time rows are genuinely all-day.
                                                    // Multiple windows stack vertically in the tooltip.
                                                    const fmtScope = (d: { startMinutes: number | null; endMinutes: number | null }) =>
                                                        d.startMinutes == null || d.endMinutes == null
                                                            ? 'All day'
                                                            : `${fmtCapTime(d.startMinutes)}–${fmtCapTime(d.endMinutes)}`;
                                                    const lines = door.dedications.length > 0
                                                        ? door.dedications.map((d) => `${d.label}: ${fmtScope(d)}`)
                                                        : door.dedicatedTo.split(', ').filter(Boolean).map((t) => `${t}: All day`);
                                                    return (
                                                        <span
                                                            title={lines.join('\n')}
                                                            className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium min-w-0"
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                                            <span className="truncate">{door.dedicatedTo}-Dedicated</span>
                                                        </span>
                                                    );
                                                })()}
                                            </span>
                                        )}
                                    </div>
                                    {door.capabilities.length > 0 && (
                                        <div className="flex items-center gap-1 ml-auto">
                                            {door.capabilities.map((cap) => (
                                                <span
                                                    key={cap}
                                                    title={cap}
                                                    className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground font-medium uppercase tracking-wide"
                                                >
                                                    {cap.slice(0, 4)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                    );
                                })()}

                                {/* Time grid + appointments */}
                                <div
                                    className={cn(
                                        'relative flex-1 cursor-crosshair',
                                        dropHover?.kind === 'row' && dropHover.dockDoorId === door.dockDoorId
                                            && 'bg-emerald-50 outline outline-2 -outline-offset-2 outline-emerald-400',
                                    )}
                                    style={{ width: totalWidth }}
                                    data-row-grid={door.dockDoorId}
                                    onMouseDown={(e) => {
                                        // ⭐ THE QUEUE ROW ACCEPTS A DRAG LIKE ANY OTHER, and
                                        // reports its door as null rather than the sentinel 0.
                                        // Pete, 2026-08-22: "is there a reason that I cannot
                                        // drag/create an appointment in Unassigned?" — there was
                                        // not. The whole chain already spoke null: the PATCH body
                                        // documents dockDoorId null as "send back to the unassigned
                                        // queue", PrefillInfo reads null as "unassigned bucket",
                                        // and WeekBoard has always mapped its UNASSIGNED row to
                                        // null. ⛔ ONLY THE DAY GRID REFUSED, on the strength of a
                                        // comment written when this row was display-only.
                                        handleRowMouseDown(e, door.dockDoorId === UNASSIGNED_DOOR_ID ? null : door.dockDoorId);
                                    }}
                                    onDragOver={(e) => {
                                        // The UNASSIGNED row is a holding area, not a door --
                                        // the same reason it refuses click-to-create.
                                        if (door.dockDoorId === UNASSIGNED_DOOR_ID) return;
                                        if (!onDropCheckInInSlot || !isCheckInDrag(e)) return;
                                        // An appointment block under the cursor owns the drop.
                                        if ((e.target as HTMLElement).closest('[data-appointment-block]')) return;
                                        e.preventDefault();          // required, or no drop fires
                                        e.dataTransfer.dropEffect = 'link';
                                        setDropHover({ kind: 'row', dockDoorId: door.dockDoorId });
                                    }}
                                    onDragLeave={(e) => {
                                        // Ignore bubbling from children, or the highlight flickers.
                                        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                                        setDropHover((h) => (h?.kind === 'row' && h.dockDoorId === door.dockDoorId ? null : h));
                                    }}
                                    onDrop={(e) => {
                                        setDropHover(null);
                                        if (door.dockDoorId === UNASSIGNED_DOOR_ID) return;
                                        if (!onDropCheckInInSlot) return;
                                        const checkInId = e.dataTransfer.getData(CHECKIN_DND_MIME);
                                        if (!checkInId) return;
                                        if ((e.target as HTMLElement).closest('[data-appointment-block]')) return;
                                        e.preventDefault();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = Math.max(0, Math.min(totalWidth, e.clientX - rect.left));
                                        // Snap to the facility's slot length, the same as
                                        // click-to-create. A dropped truck must not produce
                                        // an 11:47 appointment nobody asked for.
                                        const raw = (x / hourWidth) * 60 + startHour * 60;
                                        const snapped = Math.floor(raw / slotMinutes) * slotMinutes;
                                        onDropCheckInInSlot({
                                            checkInId,
                                            dockDoorId: door.dockDoorId,
                                            startLocal: minutesToLocalDateTime(snapped, date),
                                            endLocal: minutesToLocalDateTime(Math.min(snapped + slotMinutes, 24 * 60 - 1), date),
                                        });
                                    }}
                                    onContextMenu={(e) => {
                                        // Only EMPTY row space. A right-click on an
                                        // appointment belongs to that appointment's
                                        // menu, which handles its own event.
                                        if (!onRequestDoorMenu) return;
                                        if (door.dockDoorId === UNASSIGNED_DOOR_ID) return;
                                        if ((e.target as HTMLElement).closest('[data-appointment-block]')) return;
                                        e.preventDefault();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = Math.max(0, Math.min(totalWidth, e.clientX - rect.left));
                                        // Snap back to the hour: a block is an outage,
                                        // and nobody means "10:47 to 12:47".
                                        const mins = Math.floor(((x / hourWidth) * 60 + startHour * 60) / 60) * 60;
                                        onRequestDoorMenu({
                                            dockDoorId: door.dockDoorId,
                                            doorCode: door.code,
                                            x: e.clientX,
                                            y: e.clientY,
                                            startLocal: minutesToLocalDateTime(mins, date),
                                            endLocal: minutesToLocalDateTime(Math.min(mins + 120, 24 * 60 - 1), date),
                                        });
                                    }}
                                >
                                    {/* Vertical hour lines */}
                                    {Array.from({ length: totalHours }, (_, i) => (
                                        <div
                                            key={i}
                                            className="absolute top-0 bottom-0 border-l border-border/40"
                                            style={{ left: (i + 1) * hourWidth }}
                                        />
                                    ))}

                                    {/* ⛔⛤ 2026-09-22 -- THE FULL-HEIGHT VIOLET TINT IS GONE.
                                        ⭐ Pete: *"the light purple bar can be distracting. Can you
                                        make that invisible for now and just leave the top darker
                                        bar?"* ⚠️ "FOR NOW" -- the tint is suppressed, not deleted,
                                        and the geometry below still runs so restoring it is one
                                        className. A per-hour band paints one of these per hour,
                                        which is what made it unbearable rather than merely busy.
                                        ⛔⛔ THE BLOCKED HATCH SURVIVES. A window with a cap of 0 is
                                        not decoration -- it is the only thing on the board that
                                        says "nobody may book here", and it has no other marker.
                                          ** SUPPRESSING A STYLE IS NOT THE SAME AS SUPPRESSING THE
                                             ONE CASE WHERE IT CARRIES THE MEANING. ** */}
                                    {capWindows.map((w, i) => {
                                        const g = computeBandGeometry(w.startMinutes, w.endMinutes, hourWidth, startHour, endHour);
                                        if (!g) return null;
                                        const anyBlocked = w.blockedIn || w.blockedOut;
                                        return (
                                            <div
                                                key={`cap-${i}`}
                                                className={cn(
                                                    'absolute top-0 bottom-0 pointer-events-none z-0',
                                                    anyBlocked && 'bg-violet-200/40',
                                                )}
                                                style={{
                                                    left: g.left, width: g.width,
                                                    ...(anyBlocked ? {
                                                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(124,58,237,0.30) 0, rgba(124,58,237,0.30) 2px, transparent 2px, transparent 9px)',
                                                    } : {}),
                                                }}
                                            />
                                        );
                                    })}

                                    {/* Time-scoped dedication windows (behind appointments).
                                        No text label here — it overlaps appointments; the
                                        amber color + the door's "-Dedicated" badge convey it. */}
                                    {door.dedicationBands.map((b, i) => {
                                        const isDragging = !!dedDrag && dedDrag.dedicationId === b.dedicationId;
                                        const gStart = isDragging && dedPreview ? dedPreview.start : b.startMinutes;
                                        const gEnd   = isDragging && dedPreview ? dedPreview.end   : b.endMinutes;
                                        const g = computeBandGeometry(gStart, gEnd, hourWidth, startHour, endHour);
                                        if (!g) return null;
                                        const startDedDrag = (mode: 'move' | 'resize-l' | 'resize-r', e: React.MouseEvent) => {
                                            if (!onEditDedicationTimes || e.button !== 0) return;
                                            e.stopPropagation();
                                            /* ⛔ SAME RULE AS THE OTHER FOUR (2026-09-10). stopPropagation
                                               keeps the row from also reacting; it does NOTHING about the
                                               browser's default, which is to start selecting text. */
                                            e.preventDefault();
                                            dedMovedRef.current = false;
                                            setDedPreview({ start: b.startMinutes, end: b.endMinutes });
                                            setDedDrag({ mode, dedicationId: b.dedicationId, label: b.label, effectiveStartDate: b.effectiveStartDate, effectiveEndDate: b.effectiveEndDate, origStart: b.startMinutes, origEnd: b.endMinutes, startClientX: e.clientX });
                                        };
                                        return (
                                            <div key={`ded-${i}`}>
                                                <div
                                                    title={`${b.label} — Dedicated`}
                                                    className={cn(
                                                        'absolute top-0 bottom-0 bg-amber-200/35 border-x border-amber-400/40 pointer-events-none z-[2]',
                                                        isDragging && 'ring-2 ring-amber-500',
                                                    )}
                                                    style={{ left: g.left, width: g.width }}
                                                />
                                                {/* Thin interactive handle (below appointments so they overlay it):
                                                    drag = move, edges = resize, click = edit, right-click = menu. */}
                                                <div
                                                    title="Dedicated — drag to move · drag an edge to resize · click to edit · right-click for options"
                                                    className={cn(
                                                        'absolute top-0 h-4 flex items-center px-1 bg-amber-300/70 hover:bg-amber-300 z-[3] overflow-hidden truncate text-[9px] font-medium text-amber-900',
                                                        dedDrag ? 'cursor-grabbing' : 'cursor-grab',
                                                    )}
                                                    style={{ left: g.left, width: g.width }}
                                                    onMouseDown={(e) => startDedDrag('move', e)}
                                                    onClick={() => { if (dedMovedRef.current) { dedMovedRef.current = false; return; } onOpenDedications?.(); }}
                                                    onContextMenu={(e) => {
                                                        if (!onRequestDedicationMenu) return;
                                                        e.preventDefault();
                                                        onRequestDedicationMenu({
                                                            dedicationId: b.dedicationId, label: b.label,
                                                            startMinutes: b.startMinutes, endMinutes: b.endMinutes,
                                                            effectiveStartDate: b.effectiveStartDate, effectiveEndDate: b.effectiveEndDate,
                                                            x: e.clientX, y: e.clientY,
                                                        });
                                                    }}
                                                >
                                                    {onEditDedicationTimes && (
                                                        <>
                                                            <div className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-amber-600/50" onMouseDown={(e) => startDedDrag('resize-l', e)} onClick={(e) => e.stopPropagation()} />
                                                            <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-amber-600/50" onMouseDown={(e) => startDedDrag('resize-r', e)} onClick={(e) => e.stopPropagation()} />
                                                        </>
                                                    )}
                                                    <span className="pointer-events-none">{b.label}</span>
                                                    {isDragging && dedPreview && (
                                                        <span className="absolute top-4 left-0 whitespace-nowrap rounded bg-amber-700 px-1 text-[9px] font-semibold text-white z-[4]">
                                                            {fmtHM(dedPreview.start)}–{fmtHM(dedPreview.end)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Create-drag preview (only on this row) */}
                                    {activeDrag &&
                                        activeDrag.kind === 'create' &&
                                        activeDrag.dockDoorId === door.dockDoorId && (() => {
                                            const fromX = Math.min(activeDrag.startX, activeDrag.currentX);
                                            const toX = Math.max(activeDrag.startX, activeDrag.currentX);
                                            return (
                                                <div
                                                    className="absolute top-1 bottom-1 rounded-sm bg-primary/30 border-2 border-primary border-dashed pointer-events-none z-20"
                                                    style={{ left: fromX, width: Math.max(toX - fromX, 2) }}
                                                />
                                            );
                                        })()}

                                    {/* Appointment blocks */}
                                    {rowAppts.map((appt) => {
                                        const block = computeBlockGeometry(appt, date, hourWidth, startHour, endHour, spanOf);
                                        if (!block) return null;
                                        // Hide the original block while it's being dragged — we render
                                        // the ghost separately overlaid on the grid
                                        const isBeingDragged =
                                            activeDrag?.kind === 'move' &&
                                            activeDrag.appt.appointmentId === appt.appointmentId;
                                        const isResizing =
                                            activeDrag?.kind === 'resize' &&
                                            activeDrag.appt.appointmentId === appt.appointmentId;
                                        // While resizing, show the block growing/shrinking live.
                                        // Normal appts floor at one slot; blocks aren't slot-bound,
                                        // so they floor at the fine grid (SNAP_MINUTES).
                                        const minResizeStep = appt.direction === 'BLOCK' ? SNAP_MINUTES : slotMinutes;
                                        const minResizePx = (minResizeStep / 60) * hourWidth;
                                        let liveLeft = block.left;
                                        let liveWidth = block.width;
                                        if (isResizing) {
                                            const delta = activeDrag.pointerX - activeDrag.pointerStartX;
                                            if (activeDrag.edge === 'end') {
                                                liveWidth = Math.max(minResizePx, activeDrag.origWidth + delta);
                                            } else {
                                                liveWidth = Math.max(minResizePx, activeDrag.origWidth - delta);
                                                // keep the right (end) edge pinned
                                                liveLeft = activeDrag.origLeft + activeDrag.origWidth - liveWidth;
                                            }
                                        }
                                        // Resize is only offered on doored, non-terminal appts.
                                        const canResize =
                                            !!onMoveAppointment &&
                                            // ⛔ NOTHING TO RESIZE ON A PACKED CHIP. Its width is a
                                            // token, not a duration — dragging its edge would be
                                            // editing a number that does not exist.
                                            !block.packed &&
                                            appt.dockDoorId != null &&
                                            appt.statusCode !== 'DEPARTED' &&
                                            appt.statusCode !== 'CANCELLED' &&
                                            appt.statusCode !== 'NOSHOW' &&
                                            appt.statusCode !== 'ARRIVED';
                                        // Vertical position within the door row = the appt's lane.
                                        const lane = laneOf?.get(appt.appointmentId) ?? 0;
                                        const blockTop = lane * LANE_HEIGHT_PX + 2;
                                        const blockHeight = LANE_HEIGHT_PX - 4;
                                        return (
                                            <button
                                                key={appt.appointmentId}
                                                type="button"
                                                data-appointment-block
                                                /**
                                                  * ⭐ BELT AND BRACES WITH THE
                                                  * `preventDefault()` in the handler.
                                                  * ⚠️ A future edit that adds another
                                                  * early return above it would bring the
                                                  * blue-page bug straight back; this
                                                  * class survives that.
                                                  */
                                                onMouseDown={(e) => handleApptMouseDown(e, appt, idx)}
                                                onDragOver={(e) => {
                                                    // ⭐ A5 — an order, by its own MIME.
                                                    if (isWmsOrderDrag(e.dataTransfer)) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const no = wmsOrderRefusal(appt);
                                                        e.dataTransfer.dropEffect = no ? 'none' : 'link';
                                                        setDropHover(no
                                                            ? { kind: 'reject', appointmentId: appt.appointmentId }
                                                            : { kind: 'order', appointmentId: appt.appointmentId });
                                                        return;
                                                    }
                                                    // ⭐ #150 — paperwork first, because it
                                                    // is the cheaper test and the two are
                                                    // mutually exclusive by MIME type.
                                                    if (isPaperworkDragEvent(e)) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const no = paperworkRefusal(appt);
                                                        e.dataTransfer.dropEffect = no ? 'none' : 'copy';
                                                        setDropHover(no
                                                            ? { kind: 'reject', appointmentId: appt.appointmentId }
                                                            : { kind: 'paperwork', appointmentId: appt.appointmentId });
                                                        return;
                                                    }
                                                    if (!isCheckInDrag(e)) return;
                                                    if (!canAcceptCheckIn(appt)) {
                                                        // Claim the event anyway so the cursor can say NO
                                                        // here, rather than the row silently accepting a
                                                        // drop the scheduler aimed at this block.
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        e.dataTransfer.dropEffect = 'none';
                                                        setDropHover({ kind: 'reject', appointmentId: appt.appointmentId });
                                                        return;
                                                    }
                                                    e.preventDefault();
                                                    e.stopPropagation();   // this block owns it, not the row
                                                    e.dataTransfer.dropEffect = 'link';
                                                    setDropHover({ kind: 'appt', appointmentId: appt.appointmentId });
                                                }}
                                                onDragLeave={() => setDropHover((h) =>
                                                    ((h?.kind === 'appt' || h?.kind === 'paperwork' || h?.kind === 'order' || h?.kind === 'reject')
                                                        && h.appointmentId === appt.appointmentId ? null : h))}
                                                onDrop={(e) => {
                                                    setDropHover(null);

                                                    // ---- ⭐ A WMS ORDER (A5) ----
                                                    if (isWmsOrderDrag(e.dataTransfer)) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const no = wmsOrderRefusal(appt);
                                                        if (no) { onDropRefused?.(no); return; }
                                                        const order = readWmsOrderDrag(e.dataTransfer);
                                                        // ⚠️ Reported, not ignored - a drag that does
                                                        // nothing reads as a broken feature.
                                                        if (!order) {
                                                            onDropRefused?.('That drag carried no order to attach.');
                                                            return;
                                                        }
                                                        onDropWmsOrderOnAppointment!({ order, appt });
                                                        return;
                                                    }

                                                    // ---- ⭐ PAPERWORK (#150) ----
                                                    if (isPaperworkDragEvent(e)) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const no = paperworkRefusal(appt);
                                                        if (no) { onDropRefused?.(no); return; }
                                                        const payload = readPaperworkDrag(e.dataTransfer);
                                                        // ⚠️ A malformed payload is reported, not
                                                        // ignored. A drag that silently does nothing
                                                        // is indistinguishable from a broken feature.
                                                        if (!payload) {
                                                            onDropRefused?.('That drag carried nothing to file.');
                                                            return;
                                                        }
                                                        onDropPaperworkOnAppointment!({ payload: { items: payload }, appt });
                                                        return;
                                                    }

                                                    const refusal = checkInRefusal(appt);
                                                    if (refusal) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onDropRefused?.(refusal);
                                                        return;
                                                    }
                                                    const checkInId = e.dataTransfer.getData(CHECKIN_DND_MIME);
                                                    if (!checkInId) return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    /* ⭐ Empty when the arrival is unmatched, which is the
                                                       ordinary case — see CHECKIN_FROM_APPT_MIME. */
                                                    const fromAppointmentNumber =
                                                        e.dataTransfer.getData(CHECKIN_FROM_APPT_MIME) || null;
                                                    onDropCheckInOnAppointment!({ checkInId, appt, fromAppointmentNumber });
                                                }}
                                                onContextMenu={(e) => handleApptContextMenu(e, appt)}
                                                /**
                                                 * ⭐⭐ DOUBLE CLICK **OPENS** (decision 190/191).
                                                 * ⛔ A REAL `onDoubleClick`, unlike the single click,
                                                 * which is synthesised from drag-end — the browser
                                                 * fires this one natively and it cannot be confused
                                                 * with a reschedule.
                                                 * ⚠️ The single fires first, twice, so the load is
                                                 * already scoped when this opens. Accepted
                                                 * deliberately; see the prop's note.
                                                 * ⛔ `stopPropagation` or the door row beneath takes
                                                 * it as a double-click on empty slot time.
                                                 */
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectAppointment?.(appt);
                                                }}
                                                onMouseEnter={(e) => {
                                                    handleApptMouseEnter(e, appt);
                                                    /* ⛔ A5: no rail re-scope mid order-drag - see `wmsOrderDragInProgress`. */
                                                    if (wmsOrderDragInProgress()) return;
                                                    onHoverAppointment?.(String(appt.appointmentId));
                                                }}
                                                onMouseLeave={() => {
                                                    handleApptMouseLeave();
                                                    onHoverAppointment?.(null);
                                                }}
                                                className={cn(
                                                    'absolute z-[5] select-none rounded-sm border-l-2 px-2 py-0.5 text-xs text-left overflow-hidden hover:brightness-110 hover:z-10 transition-all shadow-sm cursor-grab active:cursor-grabbing',
                                                    (appt.direction === 'BLOCK'
                                                        && appt.statusCode !== 'CANCELLED'
                                                        && appt.statusCode !== 'NOSHOW'
                                                        && appt.statusCode !== 'DEPARTED')
                                                        ? BLOCK_CLASS : STATUS_CLASSES[appt.statusCode],
                                                    isBeingDragged && 'opacity-30',
                                                    dropHover?.kind === 'appt'
                                                        && dropHover.appointmentId === appt.appointmentId
                                                        && 'ring-2 ring-emerald-500 brightness-110',
                                                    // ⭐ SKY, NOT EMERALD (#150) — filing paperwork
                                                    // is not attaching a truck, and the ring is the
                                                    // last thing seen before letting go.
                                                    dropHover?.kind === 'paperwork'
                                                        && dropHover.appointmentId === appt.appointmentId
                                                        && 'ring-2 ring-sky-500 brightness-110',
                                                    /**
                                                     * ---- ⭐⭐ TWO RINGS (decision 190, 2026-09-20) ----
                                                     *
                                                     * ⛔ SELECTION FIRST, AND THE ORDER IS THE POINT.
                                                     * A selected load that is ALSO hovered must keep the
                                                     * strong ring — `cn` merges in order, so putting the
                                                     * hover second would let the cursor visibly WEAKEN the
                                                     * mark on the thing you chose.
                                                     *
                                                     * ⭐ FULL-STRENGTH = "I picked this."
                                                     *   LIGHTER      = "my cursor is here."
                                                     * ⛔ Never the same mark: one symbol saying two things
                                                     * is how you lose track of what is actually scoped —
                                                     * which is the fault this whole decision exists to fix.
                                                     *
                                                     * ⚠️ NO `ring-offset` ON THE HOVER. The offset punches a
                                                     * white gap around the chip; at hover weight that reads
                                                     * as a rendering artefact rather than an outline.
                                                     *
                                                     * ---- ⛔⛔ WHY THE HOVER WAS INVISIBLE ON GREEN --------
                                                     *
                                                     * Pete, 2026-09-21: *"because of the green colour, the
                                                     * grey highlight around the appointment is very hard to
                                                     * see - this might need to adjust based on colour."*
                                                     *
                                                     * ⭐⭐⭐ THE SELECTED RING READS ON EVERY STATUS BECAUSE IT
                                                     * HAS **TWO TONES** — `ring-offset-1` lays a white gap
                                                     * under a near-black ring, so one of the two always
                                                     * contrasts. The hover dropped the offset for the good
                                                     * reason above and lost its second tone with it, leaving
                                                     * ONE dark grey at 40% tuned against the palest status.
                                                     *
                                                     * ⛔ AND A PER-STATUS RING COLOUR WOULD NOT FIX IT:
                                                     * migration 135 makes STATUS COLOURS CUSTOMER-CONFIGURABLE.
                                                     *   ** A FIXED HIGHLIGHT COLOUR IS A BET ON A PALETTE THE
                                                     *      CUSTOMER CAN CHANGE. ** Any single hue we pick is
                                                     *      one Settings edit away from being invisible again.
                                                     *
                                                     * ⭐ SO: the second tone goes INSIDE the chip instead of
                                                     * outside it. A white hairline at `-outline-offset-1`
                                                     * hugs the fill and never detaches the chip from its
                                                     * neighbours — which is the artefact the offset caused.
                                                     * Dark outside, light inside: readable on any fill,
                                                     * light or dark, without knowing what the fill is.
                                                     * ⚠️ Still plainly LIGHTER than the selection, which the
                                                     * ordering below keeps winning.
                                                     */
                                                    selectedAppointmentIds.has(String(appt.appointmentId))
                                                        ? 'ring-2 ring-offset-1 ring-slate-900 z-20'
                                                        : (highlightAppointmentId === String(appt.appointmentId)
                                                            || previewAppointmentIds.has(String(appt.appointmentId)))
                                                            /**
                                                             * ---- ⛔⚤ AND INVISIBLE ON **BLUE**, 2026-09-23 --------------
                                                             *
                                                             * > Pete: *"Its very hard to still see hightlighted/bordered
                                                             * > appointemtns when they ar blue."*
                                                             *
                                                             * ⛔⛔ THE TWO-TONE IDEA WAS RIGHT; THE TUNING WAS DONE AGAINST
                                                             * ONE COLOUR. Both tones were held back - the dark ring at 55%,
                                                             * the inner hairline at 1px and 75% white - because they were
                                                             * judged on GREEN, which is pale. On a saturated blue chip a 55%
                                                             * near-black ring is dark on dark, and a 1px 75% white line reads
                                                             * as a rendering artefact.
                                                             *   ** TUNING A CONTRAST RULE AGAINST ONE SAMPLE OF A CONFIGURABLE
                                                             *      PALETTE IS TUNING IT AGAINST NOTHING. **
                                                             *
                                                             * ⭐ BOTH TONES GO TO FULL STRENGTH: solid near-black outside, a
                                                             * solid 2px white line inside. The SHAPE is unchanged - dark out,
                                                             * light in - so it still cannot detach the chip from its
                                                             * neighbours, and it still names no hue, which migration 135's
                                                             * configurable status colours forbid.
                                                             * ⚠️ THE SELECTION STILL OUTRANKS IT: that one carries
                                                             * `ring-offset-1`, a white gap OUTSIDE the chip, which reads as
                                                             * lifted in a way an inside line cannot.
                                                             */
                                                            && 'ring-2 ring-slate-900 outline outline-2 -outline-offset-2 outline-white z-10',
                                                    // ⭐ A5 — VIOLET: an order, not a truck (emerald) or a document (sky).
                                                    dropHover?.kind === 'order'
                                                        && dropHover.appointmentId === appt.appointmentId
                                                        && 'ring-2 ring-violet-500 brightness-110',
                                                    dropHover?.kind === 'reject'
                                                        && dropHover.appointmentId === appt.appointmentId
                                                        && 'ring-2 ring-red-500 opacity-60 cursor-not-allowed',
                                                    isResizing && 'z-20 ring-2 ring-primary',
                                                    /**
                                                     * ⭐ A PACKED CHIP HAS TO CONTRADICT ITS OWN POSITION.
                                                     * It still lands at SOME clock position — packing cannot
                                                     * avoid that — so the treatment has to say "not a time"
                                                     * loudly enough to beat the axis: a dashed outline no
                                                     * real appointment has, plus the hatch below.
                                                     * ⛔ NOT RESIZABLE AND NOT SLOT-SHAPED: there is no
                                                     * duration here to drag.
                                                     */
                                                    block.packed && 'border-l-0 border border-dashed !pl-3.5 !pr-1 ring-1 ring-inset ring-foreground/25',
                                                )}
                                                style={{
                                                    left: liveLeft, width: liveWidth, top: blockTop, height: blockHeight,
                                                    ...((appt.direction === 'BLOCK'
                                                        && appt.statusCode !== 'CANCELLED'
                                                        && appt.statusCode !== 'NOSHOW'
                                                        && appt.statusCode !== 'DEPARTED')
                                                        ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(30,41,59,0.55) 0, rgba(30,41,59,0.55) 3px, transparent 3px, transparent 10px)' }
                                                        /**
                                                         * ⛔ NOT ON A CANCELLED CHIP. Pete, 2026-08-25: *"The
                                                         * cancelled appointment has lines on the grey background.
                                                         * The Cancelled image in ALL Status line only has a grey
                                                         * background. It looks like a mix between Cancelled and
                                                         * Blocked."*
                                                         *
                                                         * ⭐ HE IS RIGHT, AND THE REASON IS THAT THE HATCH HAS
                                                         * NOTHING LEFT TO SAY HERE. It means "this booking has no
                                                         * time" — true, but once a truck is cancelled its time is
                                                         * not a fact anybody acts on. Meanwhile a light hatch over
                                                         * translucent grey lands within a shade of the BLOCK
                                                         * treatment above, so the one thing it DID communicate was
                                                         * wrong.
                                                         *
                                                         * ⚠️ AND THE LEGEND WAS ALREADY RIGHT. The "Cancelled"
                                                         * swatch is flat grey, so the grid was contradicting the
                                                         * key printed at the top of the same screen.
                                                         *
                                                         * ⭐ THE STRIKETHROUGH STILL CARRIES IT (STATUS_CLASSES),
                                                         * as does the dashed packed outline — which is kept,
                                                         * because a dashed border reads as "no time" without
                                                         * colliding with a block's solid slate.
                                                         *
                                                         * ⚠️ NOSHOW and DEPARTED day-only chips still hatch. They
                                                         * have their own distinct colours and do not collide with
                                                         * a block the way translucent grey does; if they should
                                                         * follow, that is a separate decision.
                                                         */
                                                        : (block.packed && appt.statusCode !== 'CANCELLED')
                                                            // ⚠️ A LIGHTER HATCH THAN A BLOCK'S, deliberately — a
                                                            // day-only truck is a real booking with no time, not a
                                                            // closed window. It must read as "unscheduled", not
                                                            // "unavailable".
                                                            ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(15,23,42,0.20) 0, rgba(15,23,42,0.20) 2px, transparent 2px, transparent 7px)' }
                                                            : {}),
                                                }}
                                                /**
                                                 * ⛔ A PACKED CHIP NEVER SHOWS A TIME, not even on hover.
                                                 * `formatTitle` renders the stored span — "5:00 AM – 10:00 PM"
                                                 * — which is exactly the promise nobody made. Same rule
                                                 * `appointmentTimeLabel` follows in the rest of the app.
                                                 */
                                                title={block.packed
                                                    ? `No time assigned — ${appt.carrierCode}${appt.clientName ? ' · ' + appt.clientName : ''}. Drag onto a door to give it a slot.`
                                                    : (fetchEvents ? undefined : formatTitle(appt))}
                                            >
                                                {/**
                                                  * ⭐⭐⭐ THE FINISHED STAMP -- FIRST CHILD, ABSOLUTE, OVER
                                                  * EVERYTHING (Pete, 2026-09-24).
                                                  *
                                                  * > *"if we could put the loaded, unloaded diagnol sign on the
                                                  * > appointments, instead of the checkbox, that would be
                                                  * > great!"*
                                                  *
                                                  * ⭐ SAME WORD AND SAME -20deg AS THE FLOOR BOARD'S `DoneStamp`,
                                                  * which the floor has already learned to read. ⛔ Not the same
                                                  * COMPONENT: the board sizes its stamp for a television and
                                                  * paints inside its own stylesheet. See `LoadStateStamp`.
                                                  *
                                                  * ⛔⛔ `pointer-events-none` IS LOAD-BEARING HERE, not tidiness.
                                                  * This chip is a drag source, a resize target and a right-click
                                                  * menu host; an overlay that ate any of those would read as an
                                                  * appointment that had frozen.
                                                  *
                                                  * ⚠️ WIDTH-GATED, and the tick above takes over below the
                                                  * threshold -- a 45-minute chip cannot hold a rotated word, and
                                                  * a finished load drawing NOTHING would be indistinguishable
                                                  * from one nobody has touched.
                                                  *
                                                  * ⛔ `laborDone` ONLY, exactly as the board's stamp is `done`
                                                  * only. The loudest thing on the screen must say the most.
                                                  */}
                                                {appt.laborDone && !appt.laborActive && !appt.laborPaused
                                                    && liveWidth >= STAMP_MIN_WIDTH_PX && (
                                                    <LoadStateStamp direction={appt.direction} />
                                                )}
                                                {/**
                                                  * ⛔ A PACKED CHIP CARRIES ONE MARK AND NOTHING ELSE.
                                                  *
                                                  * ⚠️ IT FIRST SHIPPED WITH THE FULL LABEL — direction badge,
                                                  * NO TIME badge, appointment number — and at 15 minutes wide
                                                  * the container is ~15px, so `overflow-hidden` ate everything
                                                  * after the direction badge. **The one thing that had to
                                                  * survive truncation was the first thing truncated**, which
                                                  * left a chip that looked exactly like a small booking.
                                                  *
                                                  * ⭐ So the packed chip shows a single glyph — a struck-through
                                                  * clock — coloured by direction. It fits, it is not a word
                                                  * that can be half-read, and it means the same thing at any
                                                  * width. Everything else lives in the tooltip and the hover
                                                  * card, which have room.
                                                  */}
                                                {/**
                                                  * ⭐ THE GLYPH LEADS THE LABEL — it no longer replaces it.
                                                  *
                                                  * ⚠️ THE OLD LESSON STILL APPLIES, JUST INVERTED. What must
                                                  * survive truncation has to come FIRST, and at 15px that meant
                                                  * showing only this mark. At 30 minutes there is room for the
                                                  * mark AND the label, so the rule becomes an ordering rule
                                                  * rather than an exclusion: no-time glyph, then direction, then
                                                  * carrier and client, then PO and pallets. Narrow the window
                                                  * far enough and it degrades in exactly that order.
                                                  */}
                                                {block.packed && (
                                                    <span
                                                        className="absolute left-0.5 top-0.5 opacity-80"
                                                        aria-label="No time assigned"
                                                    >
                                                        <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none"
                                                             stroke="currentColor" strokeWidth="2.5"
                                                             strokeLinecap="round">
                                                            <circle cx="12" cy="12" r="8.5" />
                                                            <path d="M12 7.5V12l2.5 2" />
                                                            <path d="M4 20 20 4" />
                                                        </svg>
                                                    </span>
                                                )}
                                                {/**
                                                  * ---- ⭐⭐⭐ THE "NO WMS" MARK, LOWER RIGHT (Pete, 2026-09-27) ----
                                                  *
                                                  * > *"We are also missing the WMS icon on the appointment with a
                                                  * > circle slash showing no WMS (in lower right hand corner)"*
                                                  *
                                                  * ⛔⛔ ONLY THE **ABSENCE** DRAWS. `wmsMarkFor`'s third argument
                                                  * defaults to false and is left that way deliberately: almost every
                                                  * truck on a good day HAS an order, and a mark on the normal case is
                                                  * a mark nobody reads.
                                                  *   ** A GLYPH ON THE COMMON CASE IS WALLPAPER. IT ONLY MEANS
                                                  *      SOMETHING WHERE IT IS RARE. **
                                                  * ⭐ Which is also why it is AMBER and not red (SPEC §7.2): red is
                                                  * the no-show and the overdue, and "not linked yet" is neither.
                                                  *
                                                  * ⚠️ `null` DRAWS NOTHING, and that is the third state doing its
                                                  * job. `hasWmsOrder` is null when the query did not ask — a truck
                                                  * nobody asked about must not be stamped "no order".
                                                  *   ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. **
                                                  *
                                                  * ⛔ NOT ON A BLOCK and NOT ON A PACKED CHIP. A door outage has no
                                                  * load to match, and a packed chip carries ONE mark and nothing
                                                  * else — the rule the struck-through clock above already states,
                                                  * learned when the full label was eaten by `overflow-hidden` at
                                                  * 15px wide.
                                                  *
                                                  * ⛔⛔ `pointer-events-none`, same as the finished stamp and for the
                                                  * same reason: this chip is a drag source, a resize target and a
                                                  * right-click menu host, and an overlay that ate any of those would
                                                  * read as an appointment that had frozen.
                                                  */}
                                                {/**
                                                  * ---- ⛤⛤ IT SHIPPED AT 10px AND PETE COULD NOT READ IT ------
                                                  *
                                                  * > Pete, 2026-09-27: *"This no wms icon is wayyyyy to small. It
                                                  * > can't be the similar size of the Icons above it? Does it have
                                                  * > the WMS word in it like i asked?"*
                                                  *
                                                  * ⭐ IT DID HAVE THE WORD. `WmsMarks.tsx` draws `WMS` inside the
                                                  * ring with the slash over it, exactly as asked. ⛔ But this call
                                                  * site passed `h-2.5 w-2.5` — 10px — and the component's OWN
                                                  * header says, in as many words, *"On a Gantt chip this renders
                                                  * at h-4 w-4"*. At 10px the three letters are sub-pixel: they
                                                  * render as a grey smear inside the ring, so the mark answered
                                                  * "is there a word in it?" with "no" on the only surface he
                                                  * looks at.
                                                  *   ** A GLYPH DRAWN BELOW THE SIZE ITS OWN DOCUMENTATION
                                                  *      ASSUMES IS A DIFFERENT GLYPH. THE FEATURE WAS BUILT,
                                                  *      SHIPPED, AND INVISIBLE. **
                                                  *   ** AND THE CALL SITE CONTRADICTED THE COMPONENT IN WRITING.
                                                  *      NOTHING CAUGHT IT, BECAUSE A SIZE IS A STRING. **
                                                  *
                                                  * ---- ⭐ THE DARK PILL, SAME AS THE OTHER THREE MARKS --------
                                                  *
                                                  * Pete asked for *"similar size of the Icons above it"* — the
                                                  * truck, paperwork and licence badges on the title row. Those are
                                                  * `h-3 w-3` glyphs inside `bg-black/45` + `ring-white/25`, and
                                                  * `AppointmentHoverCard` explains why: appointment tiles are
                                                  * SATURATED status colours, and a tinted glyph vanishes on a
                                                  * same-hue tile. His screenshot is an amber-brown OUT tile with an
                                                  * `amber-600` mark on it — the exact failure that note describes.
                                                  *   ** THREE MARKS THAT MEAN THREE DIFFERENT THINGS MUST AT
                                                  *      LEAST LOOK LIKE THE SAME KIND OF THING. **
                                                  *
                                                  * ⚠️ STILL AMBER (SPEC §7.2, and it is not aesthetic — red is the
                                                  * no-show and the overdue). `amber-300`, not `amber-600`, because
                                                  * the pill behind it is now dark; the hue is unchanged, the
                                                  * lightness is chosen for its backing.
                                                  *
                                                  * ⚠️ `ringStrokeWidth` DROPS 4.5 → 3.5 (the component default).
                                                  * 4.5 existed to keep the ring visible at 10px and it ate the
                                                  * letters' clearance; at 16px the default is what leaves `WMS`
                                                  * room to read.
                                                  *
                                                  * ⛔ POSITION IS UNCHANGED — lower-right, absolute,
                                                  * `pointer-events-none`. The chip is a drag source, a resize
                                                  * target and a right-click menu host.
                                                  */}
                                                {/* ⭐ NO WMS, LOWER RIGHT — the new pill in the old place (2026-09-28).
                                                    The ring was replaced by `NoWmsBadge` (*"The no WMS icon looks
                                                    wrong?"*), briefly moved into the title row, and put back here:
                                                    Pete, *"It cannot fit on the top row. Has to go in the bottom
                                                    right"*. ⚠️ 14px tall from bottom-0.5 in a 36px chip clears
                                                    the title row — the ring was 20px and overlapped it.
                                                    ⛔ `pointer-events-none`: the chip is a drag source, a resize
                                                    target and a right-click menu host. Not on blocks or packed chips. */}
                                                {!block.packed && appt.direction !== 'BLOCK' && (
                                                    <NoWmsBadge
                                                        hasWmsOrder={appt.hasWmsOrder}
                                                        className="pointer-events-none absolute bottom-0.5 right-0.5"
                                                    />
                                                )}
                                                {(
                                                <>
                                                <div className="font-medium leading-tight truncate flex items-center gap-1">
                                                    {/* ⭐ F70: the one IN / OUT tag (DirectionTag.tsx) —
                                                        the driver cards render the same component. */}
                                                    <DirectionTag
                                                        direction={appt.direction === 'BLOCK' ? 'BLOCK'
                                                            : appt.direction === 'INBOUND' ? 'INBOUND' : 'OUTBOUND'}
                                                    />
                                                    {/* ⭐ DECISION 266 — POSSIBLE NO-SHOW. The slot has ended and no
                                                        arrival is recorded. RED, because red is the no-show and the
                                                        overdue (SPEC §7.2) — and "possible" because only a person
                                                        can say whether the truck came. Same rule as the server's list
                                                        (`lib/possibleNoShow.ts`). ⛔ Not on a packed chip: it carries
                                                        one mark and nothing else. */}
                                                    {!block.packed && isPossibleNoShow(appt, undefined, noShowGraceMinutes) && (
                                                        <span
                                                            title={noShowGraceMinutes > 0
                                                                ? `Possible no-show — the slot ended over ${noShowGraceMinutes} min ago and no arrival is recorded. Mark it from Possible no-shows in the header.`
                                                                : 'Possible no-show — the slot has ended and no arrival is recorded. Mark it from Possible no-shows in the header.'}
                                                            className="inline-flex shrink-0 items-center rounded bg-red-600 p-0.5 text-white ring-1 ring-white/60"
                                                        >
                                                            <AlarmClockOff className="h-3 w-3" aria-hidden="true" />
                                                        </span>
                                                    )}
                                                    {/* Shared with the week/month boards AND the dock
                                                        board: one "being worked" symbol everywhere. */}
                                                    {/* ⭐ ONE SLOT, THREE ANSWERS (2026-09-24). ⛔ Not two `&&` lines: they
                                                        would both be false on a truck nobody has
                                                        touched and BOTH true is impossible, so a
                                                        ternary chain states the exclusivity the
                                                        data already has.
                                                          ** TWO INDEPENDENT CONDITIONS RENDERING
                                                             INTO ONE SLOT IS A LAYOUT BUG WAITING
                                                             FOR THE DAY THEY BOTH HOLD. ** */}
                                                    {/**
                                                      * ⭐⭐⭐ THE DONE TILE WEARS THE STAMP, NOT THE TICK
                                                      * (Pete, 2026-09-24).
                                                      *
                                                      * > *"if we could put the loaded, unloaded diagnol sign on
                                                      * > the appointments, instead of the checkbox, that would
                                                      * > be great!"*
                                                      *
                                                      * ⚠️ THE TICK SURVIVES AS THE NARROW FALLBACK, AND IT
                                                      * HAS TO. A Gantt block is a fixed 36px lane whose WIDTH is
                                                      * its duration, so a 45-minute chip has nowhere to put a
                                                      * rotated word -- it would clip or smear, not shrink. The
                                                      * answer to "it does not fit" is never "draw nothing":
                                                      *   ** A FINISHED LOAD DRAWING NOTHING IS INDISTINGUISHABLE
                                                      *      FROM ONE NOBODY HAS TOUCHED. **
                                                      * ⛔ `liveWidth` IS THE LIVE VALUE, NOT `block.width` --
                                                      * it already carries any in-flight resize drag, so the
                                                      * stamp appears and disappears as the user drags the chip
                                                      * past the threshold instead of one render later.
                                                      */}
                                                    {appt.laborActive
                                                        ? <LaborPulse />
                                                        : appt.laborPaused
                                                            ? <LaborPausedMark />
                                                            : appt.laborDone
                                                                ? (liveWidth >= STAMP_MIN_WIDTH_PX
                                                                    ? null
                                                                    : <LaborDoneMark />)
                                                                : null}
                                                    <span className="truncate">
                                                        {appt.direction === 'BLOCK'
                                                            ? (appt.comments || 'Blocked')
                                                            : <>{appt.carrierCode}{(appt.clientName || appt.clientCode) ? ` · ${appt.clientName || appt.clientCode}` : ''}</>}
                                                    </span>
                                                    {/* ⭐ A CHECKED-IN TRUCK COUNTS TOO — see DriverBadge.
                                                        Attaching an arrival writes no AppointmentDriver row,
                                                        so before this the badge vanished the moment a driver
                                                        was dragged onto an appointment. */}
                                                    {/* ⭐ THE ORDER IS FIXED: truck, paperwork, licence — WHO,
                                                        then WHAT HE BROUGHT, then WHETHER WE PROVED WHO HE IS.
                                                        ⚠️ Each renders nothing when it does not apply, so a
                                                        tile grows by exactly the marks it has earned.
                                                        ⚠️ A FRAGMENT, because this branch now returns THREE
                                                        siblings — without it the parser fails 15 lines below
                                                        with "')' expected", which is not where the fault is. */}
                                                    {appt.direction !== 'BLOCK' && (
                                                        <>
                                                        <DriverBadge
                                                            appt={appt}
                                                            hasCheckIn={claimedAppointmentIds?.has(String(appt.appointmentId))}
                                                        />
                                                        <PaperworkBadge appt={appt} />
                                                        <LicenceBadge appt={appt} />
                                                        </>
                                                    )}
                                                </div>
                                                {/* ⚠️ `pr-8` while the NO WMS tag is in the corner, so the
                                                    identifier truncates BEFORE the pill instead of under it. */}
                                                <div className={cn(
                                                    'text-[10px] opacity-90 leading-tight truncate',
                                                    !block.packed && appt.direction !== 'BLOCK' && appt.hasWmsOrder === false && 'pr-8',
                                                )}>
                                                    {/* ⭐ THE IDENTIFIER, from the shared helper — this line
                                                        used to end `?? appt.appointmentNumber`, so a container
                                                        load with no PO advertised OUR number and nothing the
                                                        driver was holding. See lib/loadIdentifier.ts. */}
                                                    {loadSubLine(appt)}
                                                </div>
                                                </>
                                                )}
                                                {canResize && (
                                                    <>
                                                        <span
                                                            data-resize-handle
                                                            onMouseDown={(e) => handleApptResizeMouseDown(e, appt, 'start')}
                                                            title="Drag to change start time"
                                                            className="absolute top-0 left-0 h-full w-2 cursor-ew-resize bg-white/0 hover:bg-white/30 active:bg-white/40"
                                                        />
                                                        <span
                                                            data-resize-handle
                                                            onMouseDown={(e) => handleApptResizeMouseDown(e, appt, 'end')}
                                                            title="Drag to change end time"
                                                            className="absolute top-0 right-0 h-full w-2 cursor-ew-resize bg-white/0 hover:bg-white/30 active:bg-white/40"
                                                        />
                                                    </>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/**
                      * ---- ⛔⛔ A FACILITY WITH NO DOORS SAYS SO (Pete, 2026-09-19) ------
                      *
                      * > Pete: *"Doors didn't load when I switched facilities back to
                      * > Brighton"* — and he was looking at **BB2**, facility 257, which
                      * > has never had a dock door. Brighton (262) has 23, all active.
                      *
                      * ⛔⛔ NOTHING WAS BROKEN, AND THAT IS EXACTLY THE PROBLEM. The API
                      * returned 200 with `doors: []`, the board drew `DOORS (0)` and a lone
                      * QUEUE row, and **the screen for "this site has no doors" was
                      * byte-for-byte the screen for "the fetch died"**. A person cannot tell
                      * those apart, so he reasonably read it as a regression in the deploy he
                      * had just run and reported it as one.
                      *
                      *   ** AN EMPTY RESULT AND A FAILED ONE MUST NOT RENDER THE SAME. A
                      *      BLANK THAT COULD MEAN EITHER IS NOT INFORMATION — IT IS A
                      *      QUESTION THE SCREEN IS ASKING THE READER. **
                      *
                      * ⚠️ THE REAL ERROR PATH ALREADY SPEAKS: `calendarError` in Calendar.tsx
                      * replaces this whole board with *"Couldn't load calendar"* and a retry.
                      * So the only silent case was the successful-but-empty one, and this is
                      * it. ⛔ Which is why this must NOT be phrased as a failure — it is the
                      * positive statement that the facility has no doors configured.
                      *
                      * ⛔ `doors.length`, NOT `rows.length` — `rows` always holds the QUEUE
                      * row, so it is never 0 and this would never render. Same trap the
                      * `Doors ({doors.length})` count documents a few hundred lines up.
                      *
                      * ⚠️ THE QUEUE ROW STAYS. An arrival with no door assigned is still
                      * legitimate here, and it is still the drop target — removing it would
                      * make a real state unreachable to fix a cosmetic one.
                      */}
                    {doors.length === 0 && (
                        <div
                            className="absolute left-0 right-0 flex items-start justify-center px-6 pt-6"
                            style={{
                                top: HEADER_HEIGHT_PX + capOffset
                                     + (layout.topByDoor.get(UNASSIGNED_DOOR_ID) ?? 0)
                                     + (layout.heightByDoor.get(UNASSIGNED_DOOR_ID) ?? ROW_HEIGHT_PX),
                            }}
                        >
                            <div className="max-w-md rounded-lg border border-dashed bg-card p-4 text-center">
                                <p className="text-sm font-medium">
                                    No dock doors are set up for this facility
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    The schedule loaded correctly — this site just has no doors
                                    configured yet. Add them in Settings → Doors, or pick a
                                    different facility above.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ---- Move-drag ghost (overlaid above all rows) ---- */}
                    {activeDrag?.kind === 'move' && (() => {
                        const deltaX = activeDrag.pointerX - activeDrag.pointerStartX;
                        const deltaY = activeDrag.pointerY - activeDrag.pointerStartY;
                        const ghostLeft = activeDrag.origLeft + deltaX;
                        const ghostTop = activeDrag.origRowTop + deltaY;
                        return (
                            <div
                                className={cn(
                                    'absolute pointer-events-none z-30 rounded-sm border-2 border-primary shadow-lg',
                                    STATUS_CLASSES[activeDrag.appt.statusCode],
                                )}
                                style={{
                                    left: DOOR_LABEL_WIDTH_PX + Math.max(0, ghostLeft),
                                    top: ghostTop + 1,
                                    width: activeDrag.origWidth,
                                    height: LANE_HEIGHT_PX - 2,
                                }}
                            >
                                <div className="px-2 py-0.5 text-xs font-medium leading-tight truncate">
                                    {activeDrag.appt.carrierCode}
                                </div>
                            </div>
                        );
                    })()}

                    {/* ---- Hover guide (snaps to 15-min ticks; disappears on leave) ---- */}
                    {hoverGuideLeft !== null && (
                        <div
                            className="absolute top-0 bottom-0 z-[16] w-px bg-cyan-500/70 pointer-events-none"
                            style={{ left: hoverGuideLeft }}
                        />
                    )}

                    {/* ---- "Now" indicator ---- */}
                    {nowOffset !== null && (
                        <div
                            className="absolute top-0 bottom-0 z-15 pointer-events-none"
                            style={{ left: DOOR_LABEL_WIDTH_PX + nowOffset }}
                        >
                            <div className="absolute top-0 bottom-0 w-px bg-destructive/70" />
                            <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-destructive" />
                        </div>
                    )}
                </div>
            </div>

            {hover && (
                <HoverTimeline appt={hover.appt} x={hover.x} y={hover.y} events={hoverEvents} />
            )}
        </div>
    );
}

// ---- Hover timeline ------------------------------------------------------

// formatEventLabel + HoverTimeline USED TO BE DUPLICATED HERE.
//
// Byte-for-byte copies of the versions in AppointmentHoverCard.tsx. They drifted
// the moment the shared one gained a facts header (time / door / PO / pallets) on
// 2026-08-13: the edit went into the shared component, the day grid kept rendering
// its private copy, and the change shipped to production looking like it had done
// nothing. Pete spotted it on the live board.
//
// This is the same failure WorkTruckMark.tsx exists to prevent, and the rule is the
// same: one hover card, one event vocabulary, imported — never re-declared. If this
// file ever needs a DIFFERENT card, add a prop to the shared one.

// ---- Geometry helpers ----------------------------------------------------

/**
 * Assign each appointment in a door to a vertical "lane" so overlapping trucks
 * stack instead of hiding each other. Classic interval partitioning: sort by
 * start, then place each appointment in the lowest lane whose previous
 * occupant has already ended. Returns a map appointmentId -> laneIndex and the
 * total lane count for the door (>= 1).
 *
 * Overlap is judged on the SCHEDULED window (start/end), the same values the
 * blocks are drawn from. Appointments that don't render in the visible time
 * window still get a lane (harmless; they just won't paint).
 */
/**
 * ⭐ HOW WIDE A DAY-ONLY CHIP IS, IN MINUTES — and 15 is chosen to be WRONG.
 *
 * Pete, 2026-08-22: *"maybe you make them 15 min wide and when they move them,
 * they will get the message anyway that they have to be an hour or whatever the
 * facility is set to."*
 *
 * ⛔ THAT IS THE WHOLE ARGUMENT, AND IT IS BETTER THAN MAKING THEM AN HOUR. Every
 * facility books in 60- or 90-minute slots, so a 15-minute chip cannot be mistaken
 * for a booking — it is visibly not a slot anyone could have been given. An
 * hour-wide chip would look exactly like a real appointment and quietly become a
 * promise. ⚠️ And dragging one onto a door runs the ordinary placement flow, which
 * applies the facility's real slot length, so the chip's width never survives
 * contact with a decision.
 */
/**
 * ⭐ 30 MINUTES, NOT 15 — Pete, 2026-08-24: *"I just created 5 outbounds for Hellman
 * but now I realize, I won't be able to visually distinguish any of these with[out]
 * mouse-hovering over the appointment. Maybe we make them 30 minutes wide so we can
 * see the standard meeting info like IN, Client, PLT, etc."*
 *
 * ⛔ 15 WAS CHOSEN TO FIT A GLYPH, AND THAT WAS THE WRONG TRADE. At 15 minutes the
 * chip is ~15px, so the full label truncated and the ONE thing that had to survive —
 * the direction badge — was the first thing cut. The answer then was to show a single
 * struck-clock glyph. ⚠️ Which fits, and says nothing: five outbound Hellman trucks
 * became five identical marks, distinguishable only by hovering each one.
 *
 * ⭐ SO THE WIDTH MOVED TO FIT THE CONTENT, rather than the content shrinking to fit
 * the width. At 30 minutes the chip carries what every other chip carries.
 *
 * ⚠️ IT IS STILL NOT A DURATION. A packed chip's width is a token — the dashed border
 * and the hatch are what say "no time", and both are width-independent, which is why
 * they and not the size are the signal.
 */
const DAY_ONLY_CHIP_MINUTES = 30;

/**
 * ⛔ WHERE AN APPOINTMENT ACTUALLY SITS ON THE GRID — ONE ANSWER, USED TWICE.
 *
 * Lane assignment and pixel geometry MUST agree. They used to read the DTO
 * separately, which was fine while every appointment was drawn from its stored
 * span; the moment day-only rows are drawn somewhere other than their stored span,
 * two readers become two answers — chips packed onto one lane by the geometry and
 * spread across thirty by the lane algorithm, or the reverse.
 *
 * ⭐ SO BOTH NOW ASK THIS.
 *
 * ===========================================================================
 * ⚠️ WHY DAY-ONLY ROWS ARE NOT DRAWN FROM THEIR STORED SPAN
 * ===========================================================================
 * A day-only appointment stores the facility's whole operating window as its
 * start and end — deliberately, so capacity and daily-limit queries can see it
 * (see `appointmentTimeLabel`, which refuses to print "05:00" for the same
 * reason). ⛔ Drawn literally, thirty of them became thirty 17-hour bars covering
 * the entire schedule, each overlapping the rest and so each claiming its own
 * lane. That is the 2026-08-22 screenshot.
 *
 * ⭐ PACKED SEQUENTIALLY, THEY STOP OVERLAPPING — and the existing lane algorithm
 * collapses them from thirty lanes to one for free. The hover-card storm goes
 * with it, because the blocks no longer sit on top of one another.
 *
 * ⚠️ THE RESIDUAL AND IT IS HONEST: a packed chip still lands at SOME clock
 * position. Nothing in the layout can fix that — only the treatment can, which is
 * why the chip is hatched, carries no time in its label, and says "no time".
 */
export interface EffectiveSpan {
    startMin: number;
    endMin: number;
    /** ⭐ True when this is a packed chip rather than a real span. */
    packed: boolean;
}

export function effectiveSpans(
    appts: AppointmentDto[], date: string, startHour: number,
    /**
     * ⭐ THE FACILITY'S OPEN TIME — where the working day begins, which is where an
     * untimed truck belongs. ⛔ NOT the drawn window's left edge: that edge grows to
     * swallow blocks, capacity bands and dedications, so anchoring to it puts the
     * chips at whatever the earliest thing on the grid happens to be. Pete found
     * them at midnight for exactly that reason.
     * ⚠️ Falls back to the drawn edge when a facility has no configured hours —
     * somewhere visible beats nowhere.
     */
    operatingWindow?: { startMinutes: number; endMinutes: number },
): Map<number, EffectiveSpan> {
    const out = new Map<number, EffectiveSpan>();
    const dayStart = parseISO(`${date}T00:00:00`);
    const packFrom = operatingWindow ? operatingWindow.startMinutes : startHour * 60;
    const packUntil = operatingWindow ? operatingWindow.endMinutes : (startHour * 60) + 12 * 60;

    /**
     * ⚠️ PACKED IN A STABLE ORDER, NOT THE ARRAY'S. The appointments arrive in
     * whatever order the query returned; sorting by id means a chip does not jump
     * to a different position every time the grid refreshes, which would make them
     * impossible to point at across a reload.
     */
    /**
     * ⛔ A DAY-ONLY TRUCK THAT HAS BEEN AND GONE IS DRAWN WHERE IT ACTUALLY WAS.
     * Packing it into a "no time assigned" chip claims we do not know something we
     * do — see `hasActualSpan`. It falls through to the real-span loop below.
     */
    const dayOnly = appts.filter(a => isDayOnly(a) && !hasActualSpan(a))
        .sort((a, b) => a.appointmentId - b.appointmentId);

    /**
     * ⭐ WHEN THE ROW FILLS, WRAP — AND THE LANES DO THE REST. Chips that wrap
     * overlap the earlier ones in time, so `assignLanes` puts them on a second
     * lane by itself: rows of chips, for free, with no extra layout code.
     *
     * ⛔ WITHOUT THIS THEY WOULD SILENTLY VANISH. A chip packed past the window end
     * makes `computeBlockGeometry` return null and the truck disappears from the
     * schedule entirely — the worst possible failure for a feature whose whole job
     * is making untimed trucks visible.
     */
    const perLane = Math.max(1, Math.floor((packUntil - packFrom) / DAY_ONLY_CHIP_MINUTES));
    dayOnly.forEach((a, i) => {
        const startMin = packFrom + (i % perLane) * DAY_ONLY_CHIP_MINUTES;
        out.set(a.appointmentId, {
            startMin, endMin: startMin + DAY_ONLY_CHIP_MINUTES, packed: true,
        });
    });

    for (const a of appts) {
        if (out.has(a.appointmentId)) continue;
        /**
         * ⭐ THE ACTUAL SPAN WINS FOR A DAY-ONLY TRUCK, and only for one.
         *
         * ⛔ Its SCHEDULED span is the facility's whole operating window — stored so
         * capacity and daily-limit queries can see it, never because anybody promised
         * those hours. Drawn literally it is a 17-hour bar. Now that the real hours
         * are known, they are strictly better information.
         *
         * ⚠️ A BOOKED TIMESLOT IS LEFT ALONE. Redrawing it at its actual times would
         * quietly turn the board from a plan into a report and hide every late
         * arrival it exists to show.
         */
        const useActual = isDayOnly(a) && hasActualSpan(a);
        const startIso = useActual ? a.actualArrivalUtc! : a.scheduledStartUtc;
        const endIso = useActual ? a.actualDepartureUtc! : a.scheduledEndUtc;
        out.set(a.appointmentId, {
            startMin: differenceInMinutes(parseISO(startIso), dayStart),
            endMin: differenceInMinutes(parseISO(endIso), dayStart),
            packed: false,
        });
    }
    return out;
}

function assignLanes(
    appts: AppointmentDto[], spans: Map<number, EffectiveSpan>,
): { laneOf: Map<number, number>; laneCount: number } {
    const laneOf = new Map<number, number>();
    if (appts.length === 0) return { laneOf, laneCount: 1 };

    // ⛔ SORTED AND MEASURED BY THE EFFECTIVE SPAN, not the stored one — see
    // effectiveSpans. Reading the DTO here would put packed chips back on top of
    // one another and undo the whole fix.
    const at = (a: AppointmentDto) => spans.get(a.appointmentId);
    const sorted = [...appts].sort((a, b) => (at(a)?.startMin ?? 0) - (at(b)?.startMin ?? 0));
    // laneEnds[i] = end minute of the last appointment placed in lane i
    const laneEnds: number[] = [];
    for (const a of sorted) {
        const span = at(a);
        if (!span) continue;
        const start = span.startMin;
        const end = span.endMin;
        let placed = false;
        for (let i = 0; i < laneEnds.length; i++) {
            if (start >= laneEnds[i]) {
                laneOf.set(a.appointmentId, i);
                laneEnds[i] = end;
                placed = true;
                break;
            }
        }
        if (!placed) {
            laneOf.set(a.appointmentId, laneEnds.length);
            laneEnds.push(end);
        }
    }
    return { laneOf, laneCount: Math.max(1, laneEnds.length) };
}

function computeBlockGeometry(
    appt: AppointmentDto, date: string, hourWidth: number, startHour: number, endHour: number,
    spans?: Map<number, EffectiveSpan>,
): { left: number; width: number; packed: boolean } | null {
    // ⛔ THE SAME SOURCE THE LANES USED. See effectiveSpans.
    const span = spans?.get(appt.appointmentId);
    const dayStart = parseISO(`${date}T00:00:00`);
    const startMinutes = span
        ? span.startMin
        : differenceInMinutes(parseISO(appt.scheduledStartUtc), dayStart);
    const endMinutes = span
        ? span.endMin
        : differenceInMinutes(parseISO(appt.scheduledEndUtc), dayStart);
    const windowStartMin = startHour * 60;
    const windowEndMin = endHour * 60;
    if (endMinutes <= windowStartMin || startMinutes >= windowEndMin) return null;
    const clampedStart = Math.max(startMinutes, windowStartMin);
    const clampedEnd = Math.min(endMinutes, windowEndMin);
    const left = ((clampedStart - windowStartMin) / 60) * hourWidth;
    /**
     * ⚠️ THE 40px FLOOR IS SKIPPED FOR A PACKED CHIP. It exists so a very short
     * appointment stays clickable — but applied here it would inflate every
     * 15-minute chip back into an overlapping block and rebuild the pile-up the
     * packing just removed. ⭐ A packed chip is narrow ON PURPOSE: that is what
     * says "this is not a slot".
     */
    const raw = ((clampedEnd - clampedStart) / 60) * hourWidth;
    const width = span?.packed ? Math.max(14, raw) : Math.max(40, raw);
    return { left, width, packed: !!span?.packed };
}

// Dedication window band: same x-scale as appointments, clamped to the drawn
// window. No minimum width (unlike blocks) so it maps 1:1 to hours.
function computeBandGeometry(
    startMinutes: number, endMinutes: number, hourWidth: number, startHour: number, endHour: number,
): { left: number; width: number } | null {
    const windowStartMin = startHour * 60;
    const windowEndMin = endHour * 60;
    const clampedStart = Math.max(startMinutes, windowStartMin);
    const clampedEnd = Math.min(endMinutes, windowEndMin);
    if (clampedEnd <= clampedStart) return null;
    const left = ((clampedStart - windowStartMin) / 60) * hourWidth;
    const width = ((clampedEnd - clampedStart) / 60) * hourWidth;
    return { left, width };
}

/**
 * THE HOVER TEXT ON A SCHEDULE CHIP.
 *
 * ---- ⭐ THE CONTAINER NUMBER IS WHY THIS EXISTS IN ITS CURRENT FORM ----------
 *
 * Pete, 2026-08-27: *"need to add container number in the schedule cards. Can be
 * as a mouse hover item but we need to see that to match paperwork."*
 *
 * ⭐ THE JOB IS MATCHING A PIECE OF PAPER TO A CHIP. A scheduler holding a bill of
 * lading has a container or trailer number on it and needs to find the truck it
 * belongs to; the chip shows a carrier, a client and an appointment number, none
 * of which are printed on the paperwork he is holding.
 *
 * ⚠️ ON HOVER RATHER THAN ON THE CHIP, AND THAT WAS PETE'S OWN CALL. A chip is
 * about 130px wide at an hour's zoom — the second line already carries the
 * appointment number and the pallet count, and an eleven-character container
 * number would either wrap or truncate to nothing useful. ⛔ Hover costs no space
 * at all and the whole number is there.
 *
 * ⚠️ AND IT IS A NATIVE `title`, NOT A STYLED POPOVER. This grid is dense, chips
 * overlap, and it lives inside a scrolling pane — this panel's siblings have had
 * two z-index bugs already. The browser's own tooltip cannot be clipped by an
 * ancestor, cannot be covered by the next chip, and works on the first render.
 *
 * ⛔ LABELLED, NOT BARE. "CONT" and "REF" in front of the numbers: a scheduler
 * comparing against a document must know which of the two he is looking at, and
 * an unlabelled eleven-character string is exactly the kind of thing that gets
 * matched against the wrong field on the paperwork.
 */
function formatTitle(appt: AppointmentDto): string {
    const start = format(parseISO(appt.scheduledStartUtc), 'h:mm a');
    const end = format(parseISO(appt.scheduledEndUtc), 'h:mm a');
    const parts = [`${start}–${end}`, appt.carrierCode, appt.clientCode];
    /**
     * ⭐ THE MATCHING NUMBERS COME FIRST, before PO and pallets. The tooltip is
     * read to answer one question — "is this the truck on this document?" — and
     * the answer should be near the top of it rather than after two other facts.
     */
    if (appt.containerNumber) parts.push(`CONT ${appt.containerNumber}`);
    /**
     * ⚠️ NO TRAILER NUMBER HERE, AND THAT IS NOT AN OVERSIGHT. `AppointmentDto`
     * deliberately omits it — the column exists (migration 151) but only the driver
     * back-fill writes it, because a booking form asking for a trailer number
     * invites a guess. ⛔ Adding it to this tooltip means adding it to the DTO and
     * the server mapper, which is a decision about that rule rather than a tooltip
     * change. Logged, not smuggled in.
     */
    if (appt.poNumber) parts.push(`PO ${appt.poNumber}`);
    if (appt.referenceNumber) parts.push(`REF ${appt.referenceNumber}`);
    if (appt.palletCount != null) parts.push(`${appt.palletCount} plt`);
    parts.push(appt.statusCode);
    return parts.join(' · ');
}

/**
 * Convert a pixel-X within the time-grid to a YYYY-MM-DDTHH:mm local string.
 * Snaps to SNAP_MINUTES.
 */
function pxToLocalDateTimeString(
    x: number, date: string, hourWidth: number, startHour: number,
): string {
    const minutesFromStart = (x / hourWidth) * 60;
    const snapped = Math.round(minutesFromStart / SNAP_MINUTES) * SNAP_MINUTES;
    const totalMinutes = startHour * 60 + snapped;
    return minutesToLocalDateTime(totalMinutes, date);
}

function snapPxToMinutes(px: number, hourWidth: number): number {
    const minutesFromStart = Math.max(0, (px / hourWidth) * 60);
    const snapped = Math.round(minutesFromStart / SNAP_MINUTES) * SNAP_MINUTES;
    return (snapped / 60) * hourWidth;
}

/** minutes-since-midnight -> "10:00 AM" (facility-local). */
function fmtCapTime(min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60;
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtHM(m: number): string {
    const h = Math.floor(m / 60), mm = m % 60;
    const hr = ((h + 11) % 12) + 1, ap = h < 12 ? 'a' : 'p';
    return mm === 0 ? `${hr}${ap}` : `${hr}:${String(mm).padStart(2, '0')}${ap}`;
}

function minutesToLocalDateTime(totalMinutes: number, date: string): string {
    const safeMin = Math.max(0, Math.min(24 * 60 - 1, totalMinutes));
    const hour = Math.floor(safeMin / 60);
    const minute = safeMin % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date}T${pad(hour)}:${pad(minute)}`;
}
